import React, { useEffect, useState, useRef, Children, Fragment, lazy, memo, createElement, Component } from 'react';
import { Shield, Zap, Check, ArrowRight, AlertTriangle, Download, ChevronDown, ChevronUp, Clock, CreditCard, Package, Star, X, Minus, MessageSquare, AlertCircle, Brain, DollarSign, BookX, Video, HardHat, Flame, Building2, CircuitBoard, Cpu, Settings, Target, TrendingUp, Award, GitBranch, Lightbulb, Headphones, ChevronLeft, ChevronRight, Eye, Ticket, Ruler, Search, Lock, BarChart3, Banknote, Globe, BookOpen } from 'lucide-react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { useMetaPixel } from '../hooks/useMetaPixel';
import { usePurchaseNotifications } from '../hooks/usePurchaseNotifications';
import { PurchaseNotification } from '../components/PurchaseNotification';
import { VerticalCarousel } from '../components/VerticalCarousel';
import { HorizontalCarousel } from '../components/HorizontalCarousel';
import { BookStackFan } from '../components/BookStackFan';
const heroBonusBooks = [{
  src: 'https://cdn.magicpatterns.com/uploads/8hCkxvV695qrhWRJUeEp3V/Diseno_sin_titulo_(7).webp',
  alt: 'PLC MASTER',
  position: 'top-left' as const
}, {
  src: 'https://cdn.magicpatterns.com/uploads/jMegiRm2EkcJHgkYsr55Yo/A_detailed,_premium_202604151029.png',
  alt: 'Variadores y Sensores',
  position: 'top-right' as const
}, {
  src: 'https://cdn.magicpatterns.com/uploads/urAD5W6FULjmkd4Bkb2VES/Diseno_sin_titulo_(7)_(2).webp',
  alt: 'Bono 3 Protocolo de Energía',
  position: 'bottom-left' as const
}, {
  src: 'https://cdn.magicpatterns.com/uploads/vsVq8bhiUB6NysBrB8mjAQ/Diseno_sin_titulo_(7)_(1).webp',
  alt: 'Curso Paneles Solares',
  position: 'bottom-right' as const
}];
const heroPositionConfig = {
  'top-left': {
    x: '-55%',
    y: '-22%',
    rotate: -6,
    z: 12
  },
  'top-right': {
    x: '55%',
    y: '-22%',
    rotate: 6,
    z: 11
  },
  'bottom-left': {
    x: '-60%',
    y: '18%',
    rotate: -4,
    z: 10
  },
  'bottom-right': {
    x: '60%',
    y: '18%',
    rotate: 4,
    z: 9
  }
};
// FAQ Component
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden bg-white" data-id="element-54" data-mp="3e7jz9ubke07m">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors" data-id="element-55" data-mp="396mhvjydpa76">
        <span className="font-medium text-gray-900 pr-4" data-id="element-56" data-mp="3j5v8c2u1c6os">{question}</span>
        {isOpen ? <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" data-id="element-57" data-mp="3j3r5lo7vhdq3" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-58" data-mp="qt3pbrisata" />}
      </button>
      {isOpen && <div className="px-6 py-4 bg-gray-50 border-t border-gray-200" data-id="element-59" data-mp="3swpwjcqudgcv">
          <p className="text-gray-700 leading-relaxed" data-id="element-60" data-mp="apikf17ojp7y">{answer}</p>
        </div>}
    </div>;
}
// Image Carousel Component
function ImageCarousel({
  images
}: {
  images: Array<{
    image: string;
    caption: string;
  }>;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  useEffect(() => {
    if (isPaused) return;
    let interval: NodeJS.Timeout;
    const timer = setTimeout(() => {
      interval = setInterval(() => {
        setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
      }, 4000);
    }, 3000);
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [images.length, isPaused]);
  const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 5000);
    if ('touches' in e) {
      setTouchStart(e.targetTouches[0].clientX);
    } else {
      setTouchStart((e as React.MouseEvent).clientX);
    }
  };
  const handleTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
    if ('touches' in e) {
      setTouchEnd(e.targetTouches[0].clientX);
    } else {
      setTouchEnd((e as React.MouseEvent).clientX);
    }
  };
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
    if (distance < -50) setCurrentIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
    setTouchEnd(0);
    setTouchStart(0);
  };
  return <div className="relative max-w-2xl mx-auto touch-pan-y" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onMouseDown={handleTouchStart} onMouseMove={handleTouchMove} onMouseUp={handleTouchEnd} onMouseLeave={handleTouchEnd} data-id="element-61" data-mp="2t1lnk8n4ke8w">
      <div className="relative bg-gray-50 border-2 border-gray-200 rounded-xl overflow-hidden shadow-lg select-none cursor-grab active:cursor-grabbing" data-id="element-62" data-mp="2j58wliiysehu">
        <div className="aspect-[3/4] relative" data-id="element-63" data-mp="hbjaiij2c09s">
          <img src={images[currentIndex].image} alt={images[currentIndex].caption} className="w-full h-full object-cover transition-opacity duration-500 pointer-events-none" loading="lazy" decoding="async" width={640} height={853} data-id="element-64" data-mp="hcuk4yz6w28t" />
        </div>
        <div className="p-4 bg-white border-t border-gray-100" data-id="element-65" data-mp="ykvfmjq2rr0v">
          <p className="text-sm font-medium text-gray-700 text-center" data-id="element-66" data-mp="ynpxq4lm8zr4">
            {images[currentIndex].caption}
          </p>
        </div>
      </div>
      <div className="flex justify-center gap-2 mt-6" data-id="element-67" data-mp="6v9189es4wkr">
        {images.map((_, index) => <button key={index} onClick={() => {
        setCurrentIndex(index);
        setIsPaused(true);
        setTimeout(() => setIsPaused(false), 5000);
      }} className={`h-2 rounded-full transition-all duration-300 ${index === currentIndex ? 'bg-blue-600 w-8' : 'bg-gray-300 w-2'}`} aria-label={`Ir a imagen ${index + 1}`} data-id="element-68" data-mp="av9is2x6dnw1" data-mpi={index} />)}
      </div>
    </div>;
}
// Dynamic Stats Hook
function useDynamicStats() {
  const [viewers, setViewers] = useState(47);
  const [urgencySpots, setUrgencySpots] = useState(15);
  const [upsellSpots, setUpsellSpots] = useState(() => {
    const saved = localStorage.getItem('upsellSpots');
    return saved ? Math.max(2, parseInt(saved, 10)) : 8;
  });
  const [mainBuyers, setMainBuyers] = useState(() => {
    const saved = localStorage.getItem('mainBuyers');
    return saved ? parseInt(saved, 10) : 3251;
  });
  const [mainSpots, setMainSpots] = useState(15);
  const [comboBuyers, setComboBuyers] = useState(() => {
    const saved = localStorage.getItem('comboBuyers');
    return saved ? parseInt(saved, 10) : 5162;
  });
  const [comboSpots, setComboSpots] = useState(7);
  // Persist buyers and upsellSpots to localStorage
  useEffect(() => {
    localStorage.setItem('mainBuyers', mainBuyers.toString());
  }, [mainBuyers]);
  useEffect(() => {
    localStorage.setItem('comboBuyers', comboBuyers.toString());
  }, [comboBuyers]);
  useEffect(() => {
    localStorage.setItem('upsellSpots', upsellSpots.toString());
  }, [upsellSpots]);
  useEffect(() => {
    let viewerInterval: NodeJS.Timeout;
    let mainBuyersInterval: NodeJS.Timeout;
    let comboBuyersInterval: NodeJS.Timeout;
    let urgencySpotsInterval: NodeJS.Timeout;
    let upsellSpotsInterval: NodeJS.Timeout;
    // Defer non-critical state updates to reduce TBT
    const timer = setTimeout(() => {
      // Viewers fluctuation (40-63 range)
      viewerInterval = setInterval(() => {
        setViewers(prev => {
          const change = Math.floor(Math.random() * 5) - 2;
          return Math.max(40, Math.min(63, prev + change));
        });
      }, 3000 + Math.random() * 4000);
      // Profesionales (social proof): increase 1-3 every 10s
      mainBuyersInterval = setInterval(() => {
        const increment = Math.floor(Math.random() * 3) + 1;
        setMainBuyers(prev => prev + increment);
      }, 10000);
      comboBuyersInterval = setInterval(() => {
        const increment = Math.floor(Math.random() * 3) + 1;
        setComboBuyers(prev => prev + increment);
      }, 10000);
      // Card 1 cupos (scarcity): decrease 1 every 15-40s, min 2
      urgencySpotsInterval = setInterval(() => {
        setUrgencySpots(prev => Math.max(2, prev - 1));
      }, 15000 + Math.random() * 25000);
      // Upsell cupos (scarcity): decrease 1 every 120-180s, min 2
      upsellSpotsInterval = setInterval(() => {
        setUpsellSpots(prev => Math.max(2, prev - 1));
      }, 120000 + Math.random() * 60000);
    }, 3000);
    return () => {
      clearTimeout(timer);
      clearInterval(viewerInterval);
      clearInterval(mainBuyersInterval);
      clearInterval(comboBuyersInterval);
      clearInterval(urgencySpotsInterval);
      clearInterval(upsellSpotsInterval);
    };
  }, []);
  return {
    viewers,
    urgencySpots,
    upsellSpots,
    mainBuyers,
    mainSpots,
    comboBuyers,
    comboSpots
  };
}
// Current Date Hook
function useCurrentDate() {
  const [dateString, setDateString] = useState('');
  const [shortDate, setShortDate] = useState('');
  const [weekdayDate, setWeekdayDate] = useState('');
  useEffect(() => {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    };
    setDateString(now.toLocaleDateString('es-AR', options));
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    setShortDate(`${dd}/${mm}/${yyyy}`);
    // Weekday + day + month (e.g. "Lunes 16 de Marzo")
    const weekdays = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    setWeekdayDate(`${weekdays[now.getDay()]} ${now.getDate()} de ${months[now.getMonth()]}`);
  }, []);
  return {
    dateString,
    shortDate,
    weekdayDate
  };
}
// Review Form Component
function ReviewForm({
  onSubmitReview
}: {
  onSubmitReview: (review: {
    name: string;
    text: string;
    stars: number;
  }) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const handleSubmit = () => {
    if (!name.trim()) {
      setError('Por favor ingresá tu nombre');
      return;
    }
    if (rating === 0) {
      setError('Por favor seleccioná una calificación');
      return;
    }
    if (!comment.trim()) {
      setError('Por favor escribí un comentario');
      return;
    }
    setError('');
    setShowSuccess(true);
    onSubmitReview({
      name: name.trim(),
      text: comment.trim(),
      stars: rating
    });
    setName('');
    setComment('');
    setRating(0);
    setTimeout(() => {
      setShowSuccess(false);
      setIsOpen(false);
    }, 3000);
  };
  if (!isOpen) {
    return <div className="text-center mt-8" data-id="element-69" data-mp="2s0r275shuwif">
        <button onClick={() => setIsOpen(true)} className="text-gray-500 hover:text-blue-600 text-sm font-medium underline transition-colors" data-id="element-70" data-mp="2rzpea0qdqfja">
          ¿Compraste? Dejá tu reseña
        </button>
      </div>;
  }
  return <div className="bg-white border border-gray-200 rounded-xl p-6 mt-8 max-w-2xl mx-auto shadow-sm relative animate-fade-in-up" data-id="element-71" data-mp="2j1nibzli6d2v">
      {showSuccess && <div className="absolute top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium z-10" data-id="element-72" data-mp="3borinr9ni38b">
          <p data-id="element-73" data-mp="36oc0mmefc7k0">¡Gracias por tu reseña!</p>
        </div>}
      <div className="flex justify-between items-center mb-4" data-id="element-74" data-mp="31qammfvuuyfe">
        <h4 className="font-bold text-gray-900 flex items-center gap-2" data-id="element-75" data-mp="31pseolztyvrn">
          <MessageSquare className="w-5 h-5 text-blue-600" data-id="element-76" data-mp="1vb9mrz3plw3j" />
          Dejá tu reseña
        </h4>
        <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-600" data-id="element-77" data-mp="1qa3to06506th">
          <X className="w-5 h-5" data-id="element-78" data-mp="1rs2dwbp7cks6" />
        </button>
      </div>
      <div className="space-y-4" data-id="element-79" data-mp="1wrf2b0vq1zwv">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-80" data-mp="1wrndmoc74x5y">
          <input type="text" placeholder="Tu nombre" value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" data-id="element-81" data-mp="1irmepefhrzt4" />
          <div className="flex items-center gap-1 px-4 py-2 border border-gray-300 rounded-lg bg-white" data-id="element-82" data-mp="1911b1ckyo53c">
            {[1, 2, 3, 4, 5].map(s => <Star key={s} className={`w-5 h-5 cursor-pointer transition-colors ${s <= (hoverRating || rating) ? 'fill-yellow-400 text-yellow-400 drop-shadow-[0_0_5px_rgba(250,204,21,0.4)]' : 'text-gray-300'}`} onMouseEnter={() => setHoverRating(s)} onMouseLeave={() => setHoverRating(0)} onClick={() => setRating(s)} data-id="element-83" data-mp="1dudwxey99xse" />)}
          </div>
        </div>
        <textarea placeholder="Contanos tu experiencia..." rows={3} value={comment} onChange={e => setComment(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none" data-id="element-84" data-mp="v1071o9eghjn" />
        {error && <p className="text-red-500 text-sm" data-id="element-85" data-mp="lfh4jwfucdsl">{error}</p>}
        <button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition-colors text-sm" data-id="element-86" data-mp="lc33qsmtscn9">
          Enviar reseña
        </button>
      </div>
    </div>;
}
// Offer Selector Component
function OfferSelector({
  selected,
  onChange
}: {
  selected: 'solo' | 'combo';
  onChange: (val: 'solo' | 'combo') => void;
}) {
  return <div className="flex flex-col items-center mb-12 w-full max-w-4xl mx-auto px-2" data-id="element-87" data-mp="1445p2haewh3u">
      <div className="flex flex-col md:flex-row w-full gap-4" data-id="element-88" data-mp="z6cg5ghq58zv">
        {/* Solo Maestro Button */}
        <motion.button whileHover={{
        scale: 1.01
      }} whileTap={{
        scale: 0.98
      }} onClick={() => onChange('solo')} className={`relative flex-1 px-6 py-6 md:py-8 rounded-xl font-bold text-lg md:text-xl transition-all duration-300 border-2 uppercase tracking-tight ${selected === 'solo' ? 'bg-[#0a1a3a] border-blue-500 text-white animate-neon-blue z-10' : 'bg-[#0f0f0f] border-gray-800 text-gray-500 hover:text-gray-400 hover:border-gray-700 opacity-60'}`} data-id="element-89" data-mp="1d651sxee8z0z">
          {selected === 'solo' && <motion.div layoutId="selector-dot" className="absolute top-3 left-3 w-2.5 h-2.5 bg-blue-500 rounded-full shadow-[0_0_8px_rgba(59,130,246,0.8)]" data-id="element-90" data-mp="3hau34grp8ncv" />}
          SOLO MAESTRO ELÉCTRICO
        </motion.button>

        {/* Combo Button */}
        <motion.button whileHover={{
        scale: 1.01
      }} whileTap={{
        scale: 0.98
      }} onClick={() => onChange('combo')} className={`relative flex-1 px-6 py-6 md:py-8 rounded-xl font-bold text-lg md:text-xl transition-all duration-300 border-2 uppercase tracking-tight flex flex-col items-center justify-center gap-1 ${selected === 'combo' ? 'bg-black border-[#FFFF00] text-white animate-neon-yellow z-10' : 'bg-[#0a0a0a] border-gray-800 text-gray-500 hover:text-gray-400 hover:border-gray-700 opacity-60'}`} data-id="element-91" data-mp="2tmaycb7uyspj">
          {selected === 'combo' && <motion.div layoutId="selector-dot" className="absolute top-3 left-3 w-2.5 h-2.5 bg-[#FFFF00] rounded-full shadow-[0_0_8px_rgba(255,255,0,0.8)]" data-id="element-92" data-mp="1tenwsiuy2rwu" />}
          {/* Embedded Badge */}
          <div className="absolute -top-3 right-4 bg-[#FFFF00] text-black text-[10px] font-black px-3 py-1 rounded-full shadow-[0_0_15px_rgba(255,255,0,0.4)] z-10 whitespace-nowrap uppercase tracking-widest animate-badge-glow" data-id="element-93" data-mp="1jnji7k96l92j">
            MÁS ELEGIDO
          </div>

          {/* Engraved Seal Icon */}
          <div className="absolute bottom-3 right-3 opacity-20 group-hover:opacity-40 transition-opacity" data-id="element-94" data-mp="15ojtydpkyw6w">
            <Award className="w-8 h-8 md:w-10 md:h-10 text-[#FFFF00]" strokeWidth={1} data-id="element-95" data-mp="10r9yohfifqb5" />
          </div>

          <span data-id="element-96" data-mp="3008aulfdh8tx">COMBO MAESTRO ELÉCTRICO + PLC MASTER</span>
        </motion.button>
      </div>
    </div>;
}
// Lazy Video Component
function LazyVideo({
  src,
  className
}: {
  src: string;
  className?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setHasLoaded(true);
        if (videoRef.current) {
          videoRef.current.play().catch(() => {});
        }
      } else {
        if (videoRef.current) {
          videoRef.current.pause();
        }
      }
    }, {
      threshold: 0.1
    });
    if (videoRef.current) {
      observer.observe(videoRef.current);
    }
    return () => observer.disconnect();
  }, []);
  return <video ref={videoRef} className={className} loop muted playsInline preload="none" data-id="element-97" data-mp="3swhbilxrp4ud">
      {hasLoaded && <source src={src} type="video/mp4" data-id="element-98" data-mp="2cir71e2qkek7" />}
    </video>;
}
export function LandingPage() {
  const {
    trackEvent
  } = useMetaPixel();
  const {
    notification,
    isVisible
  } = usePurchaseNotifications();
  // Removed JS-based preload as it is now handled statically in index.tsx for better LCP
  const {
    viewers,
    urgencySpots,
    upsellSpots,
    mainBuyers,
    mainSpots,
    comboBuyers,
    comboSpots
  } = useDynamicStats();
  const {
    dateString: currentDate,
    shortDate,
    weekdayDate
  } = useCurrentDate();
  const [headerVisible, setHeaderVisible] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<'solo' | 'v2' | 'combo'>('v2');
  const [fearsVisible, setFearsVisible] = useState(false);
  const [problemVisible, setProblemVisible] = useState(false);
  const [problemRealVisible, setProblemRealVisible] = useState(false);
  const [decisionVisible, setDecisionVisible] = useState(false);
  const [benefitsVisible, setBenefitsVisible] = useState(false);
  const [postHeroVisible, setPostHeroVisible] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('up');
  const [isManuallyExpanded, setIsManuallyExpanded] = useState(false);
  const [isHeroFanRevealed, setIsHeroFanRevealed] = useState(false);
  const [isReadyForBonuses, setIsReadyForBonuses] = useState(false);
  // Trigger hero explosion and bonus loading after a delay to prioritize LCP
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsReadyForBonuses(true);
      // Auto-reveal after a bit more time for visual impact
      setTimeout(() => setIsHeroFanRevealed(true), 500);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);
  const [visibleTestimonials, setVisibleTestimonials] = useState(5);
  const [localReview, setLocalReview] = useState<{
    name: string;
    text: string;
    stars: number;
  } | null>(null);
  const [activeBonusIndex, setActiveBonusIndex] = useState(0);
  const bonusScrollRef = useRef<HTMLDivElement>(null);
  const marqueeResumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [marqueePaused, setMarqueePaused] = useState(false);
  const marqueeContainerRef = useRef<HTMLDivElement>(null);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const isDragging = useRef(false);
  // Animation variants for the fan effect
  const fanVariants = {
    hidden: {
      x: '0%',
      y: '0%',
      rotate: 0,
      opacity: 0
    },
    visible: (pos: keyof typeof heroPositionConfig) => ({
      x: heroPositionConfig[pos].x,
      y: heroPositionConfig[pos].y,
      rotate: heroPositionConfig[pos].rotate,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15
      }
    })
  };
  // Marquee pause/resume handlers
  const pauseMarquee = () => {
    setMarqueePaused(true);
    if (marqueeResumeTimer.current) clearTimeout(marqueeResumeTimer.current);
    marqueeResumeTimer.current = setTimeout(() => {
      setMarqueePaused(false);
    }, 2000);
  };
  // Drag handlers for manual swipe
  const handleDragStart = (clientX: number) => {
    const container = marqueeContainerRef.current;
    if (!container) return;
    isDragging.current = true;
    dragStartX.current = clientX;
    dragScrollLeft.current = container.scrollLeft;
    setMarqueePaused(true);
    if (marqueeResumeTimer.current) clearTimeout(marqueeResumeTimer.current);
  };
  const handleDragMove = (clientX: number) => {
    if (!isDragging.current) return;
    const container = marqueeContainerRef.current;
    if (!container) return;
    const dx = clientX - dragStartX.current;
    container.scrollLeft = dragScrollLeft.current - dx;
  };
  const handleDragEnd = () => {
    isDragging.current = false;
    if (marqueeResumeTimer.current) clearTimeout(marqueeResumeTimer.current);
    marqueeResumeTimer.current = setTimeout(() => {
      setMarqueePaused(false);
    }, 2000);
  };
  // Handle bonus scroll for pagination dots
  const handleBonusScroll = () => {
    if (!bonusScrollRef.current) return;
    const container = bonusScrollRef.current;
    const scrollPosition = container.scrollLeft;
    const cardWidth = container.offsetWidth * 0.85; // Approximate card width on mobile
    const newIndex = Math.round(scrollPosition / cardWidth);
    if (newIndex !== activeBonusIndex) {
      setActiveBonusIndex(newIndex);
    }
  };
  // Argentine chat reviews pool (30+)
  const allChatReviews = [{
    name: 'Javier M.',
    msg: 'Che, posta que me cambió la forma de laburar. Antes conectaba de memoria y ahora entiendo qué hace cada cable 💪',
    time: '14:32',
    verified: true
  }, {
    name: 'Gustavo R.',
    msg: 'Re sirve loco. Directo al grano, nada de vueltas teóricas al pedo. Los diagramas están claritos',
    time: '09:15',
    verified: true
  }, {
    name: 'Pablo S.',
    msg: 'Me salvó las papas con un cliente. Tenía que automatizar un tanque y no le encontraba la vuelta. Con esto lo saqué de taquito 🔧',
    time: '18:47',
    verified: true
  }, {
    name: 'Andrés C.',
    msg: 'Vale cada peso hermano. Solo con la planilla de presupuesto ya se paga solo. La uso para todos los laburos',
    time: '11:03',
    verified: true
  }, {
    name: 'Matías B.',
    msg: 'Laburo en mantenimiento y esto me ordenó la cabeza mal. Antes adivinaba, ahora sé lo que hago ⚡',
    time: '16:22',
    verified: true
  }, {
    name: 'Ricardo A.',
    msg: 'Soy técnico hace 10 pirulos y siempre se aprende algo nuevo. El protocolo de verificación es un caño, lo uso siempre',
    time: '20:11',
    verified: true
  }, {
    name: 'Marcelo D.',
    msg: 'Al principio me costó un toque porque venía de cero, pero leyéndolo tranqui se entiende todo. Me salvó con un tablero de bombas',
    time: '08:45',
    verified: false
  }, {
    name: 'Fernando T.',
    msg: 'Es técnico pero se deja leer. No es esos libros aburridos de ingeniería que te dormís. Es para el que está en la obra posta',
    time: '13:20',
    verified: true
  }, {
    name: 'Jorge L.',
    msg: 'Lo imprimí y lo llevo en la caja de herramientas. Cuando tengo una duda lo miro ahí nomás. Re práctico',
    time: '17:55',
    verified: true
  }, {
    name: 'Sergio F.',
    msg: 'Está muy completo che. Estaría bueno que tenga videos, pero las explicaciones escritas son un golazo igual',
    time: '10:30',
    verified: true
  }, {
    name: 'Diego P.',
    msg: 'Ni hablar, lo mejor que compré en años. Ahora los tableros no me asustan más jaja',
    time: '15:12',
    verified: true
  }, {
    name: 'Ramiro V.',
    msg: 'Capo el que armó esto. Se nota que sabe de campo, no es un teórico de escritorio. Re recomendado 👌',
    time: '19:40',
    verified: true
  }, {
    name: 'Hernán G.',
    msg: 'Arranqué a tomar laburos más grandes gracias a esto. Antes les tenía miedo a los tableros industriales',
    time: '07:55',
    verified: true
  }, {
    name: 'Leandro M.',
    msg: 'Posta que vale la pena. Mi viejo es electricista y se lo pasé, quedó re manija también',
    time: '12:18',
    verified: false
  }, {
    name: 'Cristian R.',
    msg: 'Lo que más me sirvió fue entender la lógica de los contactores. Antes los conectaba y rezaba jaja',
    time: '21:05',
    verified: true
  }, {
    name: 'Facundo L.',
    msg: 'Che re completo. Los bonos solos ya valen más que lo que pagué. Un golazo la planilla de presupuesto',
    time: '14:50',
    verified: true
  }, {
    name: 'Nicolás S.',
    msg: 'Soy de Córdoba y acá hay mucho laburo industrial. Con esto me animé a meterme en automatización. Gracias crack 🙌',
    time: '16:33',
    verified: true
  }, {
    name: 'Martín H.',
    msg: 'Lo arranqué a leer un domingo y no lo solté. Está explicado como si te lo contara un compañero de laburo',
    time: '11:47',
    verified: true
  }, {
    name: 'Walter B.',
    msg: 'Tengo 15 años en el rubro y hay cosas que no sabía. El tema de protecciones me voló la cabeza',
    time: '09:22',
    verified: true
  }, {
    name: 'Carlos A.',
    msg: 'Muy bueno loco. Lo único que le falta son más ejemplos de variadores, pero para mando es lo mejor que vi',
    time: '18:15',
    verified: false
  }, {
    name: 'Ezequiel D.',
    msg: 'Me lo recomendó un colega y la verdad que tenía razón. Re sirve para el día a día en obra',
    time: '13:08',
    verified: true
  }, {
    name: 'Damián F.',
    msg: 'Che, el bono de paneles solares es tremendo. Estoy pensando en meterme en eso también. Todo suma 🔋',
    time: '20:30',
    verified: true
  }, {
    name: 'Lucas T.',
    msg: 'Posta que cambió mi forma de presupuestar. Antes tiraba números al voleo, ahora tengo todo ordenado',
    time: '15:42',
    verified: true
  }, {
    name: 'Alejandro N.',
    msg: 'Lo mejor es que lo podés consultar en la obra. Nada de andar buscando en YouTube a las apuradas',
    time: '08:10',
    verified: true
  }, {
    name: 'Tomás R.',
    msg: 'Soy de Mendoza, laburo en bodegas. Los circuitos de mando de las bombas ahora los entiendo de verdad',
    time: '17:25',
    verified: true
  }, {
    name: 'Ignacio C.',
    msg: 'Un amigo me dijo "compralo que no te vas a arrepentir" y tenía razón. Muy claro todo',
    time: '12:55',
    verified: false
  }, {
    name: 'Santiago M.',
    msg: 'Re piola el material. Se nota que está hecho por alguien que labura en serio, no un profesor de facultad',
    time: '19:18',
    verified: true
  }, {
    name: 'Agustín V.',
    msg: 'Che lo del protocolo de verificación es genial. Ahora antes de dar tensión reviso todo como corresponde',
    time: '10:05',
    verified: true
  }, {
    name: 'Maximiliano P.',
    msg: 'Increíble la relación precio-calidad. Con lo que sale una herramienta tenés conocimiento para toda la vida',
    time: '14:40',
    verified: true
  }, {
    name: 'Gonzalo E.',
    msg: 'Lo compré medio desconfiado y me sorprendió. Está muy bien armado, se nota el laburo que le metieron 💯',
    time: '16:58',
    verified: true
  }];
  const testimonialData = [{
    name: 'Lucas G.',
    specialty: 'Técnico Electromecánico',
    study: 'Escuela Técnica Raggio',
    comment: 'La claridad de los manuales es impresionante. Poder tener toda la lógica de PLC y mando en un solo lugar me ahorra horas de búsqueda y errores en obra.',
    image: 'https://cdn.magicpatterns.com/uploads/9REeJugtPE4hwPnc79PgSY/Una_foto_fotorrealista_202604171241.webp'
  }, {
    name: 'Juan P.',
    specialty: 'Instalador Eléctrico',
    study: 'Escuela Técnica N°1',
    comment: 'Lo que más me sirvió fue la parte de lógica de mando. Dejé de copiar esquemas y empecé a entender por qué se conecta cada cable.',
    image: 'https://cdn.magicpatterns.com/uploads/7CHbsG1wjqAEbXPkMdWWtf/Hyper-realistic,_intimate_phone_202604151004.webp'
  }, {
    name: 'Sergio L.',
    specialty: 'Electricista Matriculado',
    study: 'COPIME',
    comment: 'La inversión se recupera en el primer trabajo que hacés con seguridad. Mis clientes notan la diferencia en mi criterio técnico.',
    image: 'https://cdn.magicpatterns.com/uploads/urm1EM6Ho19epW6AzKFAHc/Photorealistic,_intimate_medium_202604151012.webp'
  }, {
    name: 'Roberto G.',
    specialty: 'Electricista Industrial',
    study: 'UTN (Univ. Tecnológica Nacional)',
    comment: 'El manual de control eléctrico me cambió la forma de encarar los tableros. Antes dudaba mucho, ahora entiendo la lógica de punta a punta.',
    image: 'https://cdn.magicpatterns.com/uploads/ocqY51CbjtDLpjCtARHT4W/Hyper-realistic,_intimate_phone_202604151010.webp'
  }, {
    name: 'Miguel A.',
    specialty: 'Técnico en Automatización',
    study: 'Centro de Formación Profesional',
    comment: 'Muy recomendado para los que recién arrancan y para los que ya tienen años. Siempre se aprende algo nuevo con estos manuales.',
    image: 'https://cdn.magicpatterns.com/uploads/uR18KAMY2DX3wu2XqJbQph/Hyper-realistic,_candid_selfie_202604150954.webp'
  }, {
    name: 'Carlos M.',
    specialty: 'Mantenimiento de Plantas',
    study: 'Instituto Técnico Superior',
    comment: 'Excelente material. Los diagramas son súper claros y me sirvieron para resolver una falla en un motor que me tenía loco.',
    image: 'https://cdn.magicpatterns.com/uploads/dDcLT75SACqVQUA8UnAgUK/Hyper-realistic,_documentary-style_photograph._202604151012.webp'
  }];
  // Scroll reveal for sections - Defer observer setup
  useEffect(() => {
    const timer = setTimeout(() => {
      const revealObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-reveal');
            revealObserver.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1
      });
      document.querySelectorAll('[data-reveal]').forEach(el => {
        revealObserver.observe(el);
      });
      // Observer for sticky bottom bar
      const stickyObserver = new IntersectionObserver(([entry]) => {
        setShowStickyBar(entry.isIntersecting || entry.boundingClientRect.top < 0);
      }, {
        threshold: 0.1
      });
      const compraSection = document.getElementById('compra');
      if (compraSection) stickyObserver.observe(compraSection);
      return () => {
        revealObserver.disconnect();
        stickyObserver.disconnect();
      };
    }, 2000);
    return () => clearTimeout(timer);
  }, []);
  // Header visibility on scroll
  useEffect(() => {
    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setScrollDirection('down');
        setIsManuallyExpanded(false);
      } else {
        setScrollDirection('up');
      }
      lastScrollY = currentScrollY;
      setHeaderVisible(currentScrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (entry.target.id === 'fears-section') setFearsVisible(true);
            if (entry.target.id === 'benefits-grid') setProblemVisible(true);
            if (entry.target.id === 'problem-real-section') setProblemRealVisible(true);
            if (entry.target.id === 'decision-section') setDecisionVisible(true);
            if (entry.target.id === 'benefits-section') setBenefitsVisible(true);
            if (entry.target.id === 'post-hero-section') setPostHeroVisible(true);
          }
        });
      }, {
        threshold: 0.15
      });
      const sections = ['fears-section', 'benefits-grid', 'problem-real-section', 'decision-section', 'benefits-section', 'post-hero-section'];
      sections.forEach(id => {
        const el = document.getElementById(id);
        if (el) observer.observe(el);
      });
      return () => observer.disconnect();
    }, 1500);
    return () => clearTimeout(timer);
  }, []);
  const scrollToPurchase = () => {
    document.getElementById('compra')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const scrollToSolution = () => {
    document.getElementById('solution')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const handlePurchaseClick = () => {
    trackEvent('InitiateCheckout');
    window.location.href = 'https://checkout.maestroelectrico.shop/cart/47885775995102:1';
  };
  const handleComboPurchaseClick = () => {
    trackEvent('InitiateCheckout');
    window.location.href = 'https://checkout.maestroelectrico.shop/cart/47989263106270:1';
  };
  const handleV2PurchaseClick = () => {
    trackEvent('InitiateCheckout');
    window.location.href = 'https://checkout.maestroelectrico.shop/cart/48410282623198:1';
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            trackEvent('ViewContent', {
              content_name: 'Bonuses Section'
            });
            observer.disconnect();
          }
        });
      }, {
        threshold: 0.5
      });
      const bonusSection = document.getElementById('bonuses');
      if (bonusSection) observer.observe(bonusSection);
      return () => observer.disconnect();
    }, 3000);
    return () => clearTimeout(timer);
  }, [trackEvent]);
  const contentImages = [{
    image: 'https://cdn.magicpatterns.com/uploads/mm7Hbj6bhqSXE22mqwyUBz/capture_260705_161753.webp',
    caption: 'Anatomía detallada de sistemas de control'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/5Vx44Tzi9mjgCGHkZ8LLij/capture_260705_161746.webp',
    caption: 'Métodos de lectura paso a paso'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/rerwMLLbH9KWXhkPYegTcE/capture_260705_161816.webp',
    caption: 'Guía completa de motores monofásicos'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/tVBrmaCpZj3RfS9PXNEFxw/capture_260705_161822.webp',
    caption: 'Lógica de arranque y funcionamiento'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/hN9pdqsDUWFExGkqERAXpm/capture_260705_161841.webp',
    caption: 'Protección y rearmado de equipos'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/6N9aigZzaNsoRe37pWdoFH/WhatsApp_Image_2026-01-17_at_13.42.51.jpg',
    caption: 'Diagramas explicados paso a paso'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/aRdF2bWiMHEmctuHXsJVR9/WhatsApp_Image_2026-01-17_at_13.42.50_(3).jpg',
    caption: 'Entendé la lógica del circuito, no solo la conexión'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/fuLknpKftbKZSC5DGAQ2tZ/WhatsApp_Image_2026-01-17_at_13.42.50_(2).jpg',
    caption: 'Casos prácticos aplicados al trabajo real'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/aGWc1fPM9DTj6kfR5oKjFr/WhatsApp_Image_2026-01-17_at_13.42.50_(1).jpg',
    caption: 'Material pensado para uso profesional'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/dZNF3VrgumsrSXiMRXLWTh/WhatsApp_Image_2026-01-17_at_13.42.49.jpg',
    caption: 'Explicaciones técnicas claras y directas'
  }];
  const showMoreTestimonials = () => {
    setVisibleTestimonials(prev => Math.min(prev + 5, allChatReviews.length));
  };
  const handleSubmitReview = (review: {
    name: string;
    text: string;
    stars: number;
  }) => {
    setLocalReview(review);
  };
  const fearItems = [{
    icon: Flame,
    title: 'Tenés miedo de quemar un motor',
    desc: 'Un error de conexión y el equipo queda inutilizado.'
  }, {
    icon: AlertCircle,
    title: 'Cuando no responde',
    desc: 'Dás tensión… y el circuito no hace lo que debería.'
  }, {
    icon: Brain,
    title: 'La inseguridad te hace quedar mal frente al cliente',
    desc: 'No por el trabajo, sino por cómo lo enfrentás.'
  }, {
    icon: DollarSign,
    title: 'Estás estancado ganando siempre lo mismo',
    desc: 'porque te falta confianza para aceptar trabajos más grandes.'
  }];
  const decisionItems = [{
    icon: CircuitBoard,
    text: 'Criterio claro para interpretar circuitos de mando',
    keyword: 'Criterio'
  }, {
    icon: Cpu,
    text: 'Lógica aplicada a motores y tableros reales',
    keyword: 'Lógica'
  }, {
    icon: Settings,
    text: 'Seguridad al conectar motores y tableros',
    keyword: 'Seguridad'
  }, {
    icon: Target,
    text: 'Confianza para tomar trabajos más grandes',
    keyword: 'Confianza'
  }, {
    icon: TrendingUp,
    text: 'Crecimiento profesional real y sostenido',
    keyword: 'Crecimiento'
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 20
      }
    }
  };
  return <div className="min-h-screen bg-gray-950 font-['Poppins'] text-gray-100 selection:bg-blue-500/30" data-id="element-99" data-mp="184q7ozguxllu">
      {notification && !headerVisible && <PurchaseNotification name={notification.name} city={notification.city} pack={notification.pack} isVisible={isVisible} data-id="element-100" data-mp="vrw6jvxcpp61" />}

      {/* Header - Hidden initially, appears on scroll */}
      <header className={`text-white fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${headerVisible ? 'animate-header-slide bg-[#172554] shadow-md' : 'opacity-0 -translate-y-full pointer-events-none'}`} data-id="element-101" data-mp="lui3qz4coyw7">
        <div className="container mx-auto px-4 flex justify-between items-center py-3" data-id="element-102" data-mp="1eimbrrae9o22">
          <div className="flex items-center gap-3 overflow-hidden" data-id="element-103" data-mp="19j9mz0zyq6wu">
            <div className="relative flex-shrink-0" data-id="element-104" data-mp="19lnjzfe7dt71">
              <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/20 animate-logo-pulse" data-id="element-105" data-mp="15hfql4wsvp2h">
                <img src="https://cdn.magicpatterns.com/uploads/tA6D5wwQNzXrTuuBrNPdzP/capture_260206_165744.png" alt="Maestro Eléctrico Logo" className="w-full h-full object-cover" loading="eager" decoding="async" width={40} height={40} data-id="element-106" data-mp="3ubl87abtm266" />
              </div>
              <img src="https://cdn.magicpatterns.com/uploads/vFJKXGx8omQxVsJYgcByYb/Diseno_sin_titulo_(7).webp" alt="Verified" loading="eager" decoding="async" width={16} height={16} className="absolute -top-1 -right-1 w-4 h-4 z-10 drop-shadow-sm pt-[2px] pb-[0px]" data-id="element-107" data-mp="1y7ewr0ppvp2a" />
            </div>

            <div className="relative h-10 flex items-center min-w-[200px]" data-id="element-108" data-mp="2cw1m7v925x78">
              {isVisible && notification ? <div className="flex items-center gap-2 animate-fade-in-up" data-id="element-109" data-mp="2gxwlku7fi0id">
                  <div className="bg-green-500 p-1.5 rounded-full flex-shrink-0 shadow-lg shadow-green-500/20" data-id="element-110" data-mp="235zwkz1246q7">
                    {notification.pack === 'solo' ? <Zap className="w-4 h-4 text-white fill-white" data-id="element-111" data-mp="2841gfm6b4a2q" /> : <Flame className="w-4 h-4 text-white fill-white" data-id="element-112" data-mp="282qelrwf9ce9" />}
                  </div>
                  <div className="flex flex-col justify-center" data-id="element-113" data-mp="2vssyhkq6nc1s">
                    <p className="text-[11px] md:text-[12px] font-black leading-tight tracking-tight" data-id="element-114" data-mp="30mx9cf2kk485">
                      <span className="text-green-400" data-id="element-115" data-mp="39ymmiuu898fo">
                        {notification.name}
                      </span>{' '}
                      de {notification.city}
                    </p>
                    <p className="text-[9px] md:text-[10px] text-blue-100 font-bold uppercase tracking-wider" data-id="element-116" data-mp="3jp7hm8aozutu">
                      compró{' '}
                      <span className="text-white" data-id="element-117" data-mp="2r11u5ds97rqb">
                        {notification.pack === 'solo' ? 'Maestro Eléctrico' : 'Combo Maestro + PLC'}
                      </span>
                    </p>
                  </div>
                </div> : <span className="font-bold tracking-tight text-[14px] text-blue-100/50" data-id="element-118" data-mp="310anq5j7j4c5">
                  Inscripciones abiertas
                </span>}
            </div>
          </div>
          <button onClick={scrollToPurchase} className="bg-yellow-500 hover:bg-yellow-400 text-blue-900 font-bold py-2 px-4 rounded transition-colors text-[14px] flex-shrink-0 pl-[5px] pr-[5px]" data-id="element-119" data-mp="g8h478cs6fi4">
            Ver Oferta
          </button>
        </div>
      </header>

      {/* TOP HERO IMAGE BANNER */}
      <div className="w-full leading-none" data-id="element-120" data-mp="1yjruc2yj3fq3"></div>

      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden py-4 md:py-16 bg-hero-gradient" data-id="element-121" data-mp="2d9ftaxj8m2an">
        <div className="container mx-auto px-4 relative z-10 flex flex-col items-center" data-id="element-122" data-mp="28bxjz61b5l24">
          {/* Red Pill Badge */}
          <div className="inline-flex items-center gap-2 bg-red-600 text-white px-3 py-1 rounded-full text-[9px] md:text-[11px] font-black uppercase tracking-widest mb-3 md:mb-6 shadow-lg shadow-red-600/20 animate-fade-in-up" data-id="element-123" data-mp="1kla9qe3x6jce">
            <AlertCircle className="w-3 h-3 md:w-4 md:h-4" data-id="element-124" data-mp="1pk4sxh5su9x1" />
            ATENCIÓN: ELECTRICISTAS Y TÉCNICOS
          </div>

          {/* Main Title */}
          <h1 className="text-[28px] md:text-5xl lg:text-6xl font-black mb-3 md:mb-8 leading-[1] md:leading-[1.1] text-center text-white max-w-[95%] md:max-w-5xl mx-auto uppercase tracking-tight" data-id="element-125" data-mp="34zkg12ugz1f1">
            Esta misma semana podés pasar de principiante a experto en{' '}
            <span className="text-blue-400" data-id="element-126" data-mp="3di5t5ivsz7ta">comandos eléctricos...</span>
          </h1>

          {/* Blue Highlight Info Box */}
          <div className="bg-blue-500/10 border border-blue-400/20 backdrop-blur-md rounded-xl p-4 md:p-6 mb-4 md:mb-10 max-w-3xl mx-auto text-center shadow-xl" data-id="element-127" data-mp="2ym6etqi3obfp">
            <p className="text-sm md:text-xl font-medium text-blue-50 leading-snug md:leading-relaxed" data-id="element-128" data-mp="3r8r6yt6egrkb">
              ...y sumar entre{' '}
              <span className="text-yellow-400 font-bold" data-id="element-129" data-mp="27vrcwnizlh">
                US$ 200 y US$ 500
              </span>{' '}
              a tus ganancias mensuales aceptando trabajos que hoy estás
              rechazando con una guía práctica de comandos eléctricos
              industriales.
            </p>
          </div>

          {/* Book Fan - Optimized Structure */}
          <div className="mb-4 md:mb-10 flex justify-center relative w-full items-center h-[240px] md:h-[520px]" onMouseEnter={() => setIsHeroFanRevealed(true)} onMouseLeave={() => setIsHeroFanRevealed(false)} onTouchStart={() => setIsHeroFanRevealed(!isHeroFanRevealed)} data-id="element-130" data-mp="iuit6r8je5c7">
            {/* Lightweight Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-blue-600/20 rounded-full blur-[60px] pointer-events-none" data-id="element-131" data-mp="jngev8i0ych2" />

            {/* Bonus books (Loaded only when ready) */}
            {isReadyForBonuses && heroBonusBooks.map(book => <motion.img key={book.position} src={book.src} alt={book.alt} loading="lazy" decoding="async" width={280} height={373} className="absolute w-[220px] md:w-[280px] h-auto object-contain select-none pointer-events-none" initial="hidden" animate={isHeroFanRevealed ? 'visible' : 'hidden'} variants={fanVariants} custom={book.position} style={{
            zIndex: heroPositionConfig[book.position].z,
            filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.4))'
          }} data-id="element-132" data-mp="4zmznowupbz0" />)}

            {/* Main Book - LCP Priority */}
            <img src="https://cdn.magicpatterns.com/uploads/fMZ5Fq5SNbxYwzfww2mdPS/Diseno_sin_titulo_(1).webp" alt="Control Eléctrico Libro Digital" className="relative w-full max-w-[240px] md:max-w-[340px] h-auto object-contain select-none cursor-pointer z-20" onClick={() => setIsHeroFanRevealed(!isHeroFanRevealed)} decoding="async" fetchPriority="high" loading="eager" width={340} height={453} style={{
            filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.5))',
            transform: isHeroFanRevealed ? 'scale(0.95) translateY(-5px)' : 'none',
            transition: 'transform 0.4s ease-out'
          }} data-id="element-133" data-mp="1xnutk4w84kkd" />

            {/* Subtle label */}
            <p className={`absolute bottom-0 left-1/2 -translate-x-1/2 text-[11px] md:text-xs text-blue-300/60 font-medium uppercase tracking-widest whitespace-nowrap pointer-events-none transition-opacity duration-300 ${isHeroFanRevealed ? 'opacity-100' : 'opacity-0'}`} data-id="element-134" data-mp="1spt9bgn2af7b">
              1 Manual Principal + 5 Bonos Técnicos
            </p>

            {/* Interaction hint */}
            {!isHeroFanRevealed && <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap text-blue-400/40 text-[10px] font-medium uppercase tracking-widest animate-pulse" data-id="element-135" data-mp="c5sm7jpewprv">
                Tocá el libro para ver los bonos
              </div>}
          </div>

          {/* Stars + Social proof */}
          <div className="text-center mb-12" data-id="element-136" data-mp="zubtpx0n0yiw">
            <div className="inline-flex flex-col items-center p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm" data-id="element-137" data-mp="zt0ijc4v4ohr">
              <p className="text-sm md:text-base font-semibold text-blue-100/80 mb-3" data-id="element-138" data-mp="19t16xchl9ust">
                Del cableado básico al control industrial: Dominá el sistema
                completo.
              </p>
              <div className="flex items-center justify-center gap-3 mb-2" data-id="element-139" data-mp="3bk4dnqocyp83">
                <div className="flex items-center gap-1" data-id="element-140" data-mp="3bm8sh3nsp8n5">
                  {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-5 h-5 fill-yellow-400 text-yellow-400 drop-shadow-[0_0_8px_rgba(234,179,8,0.5)]" data-id="element-141" data-mp="36ne13dbribrf" />)}
                </div>
                <span className="text-sm font-bold text-yellow-400" data-id="element-142" data-mp="1r2mv0hb8asg3">
                  4.9/5 Calificación
                </span>
              </div>
              <p className="text-xs md:text-sm font-bold text-blue-400 uppercase tracking-[0.15em]" data-id="element-143" data-mp="1cbcxqs16axfu">
                Más de 6.250 profesionales ya lo usan en toda Latinoamérica
              </p>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center mt-4" data-id="element-144" data-mp="17ffg8ie6bl1h">
            <button onClick={scrollToPurchase} className="btn-shine bg-yellow-500 hover:bg-yellow-400 text-blue-900 text-xl md:text-3xl font-black py-6 px-12 rounded-xl shadow-[0_20px_50px_rgba(234,179,8,0.3)] transition-all inline-flex items-center gap-3 uppercase tracking-tight" data-id="element-145" data-mp="17c9qr21muh58">
              QUIERO EMPEZAR AHORA
              <ArrowRight className="w-6 h-6 md:w-8 md:h-8" strokeWidth={3} data-id="element-146" data-mp="2srp6nqnstn40" />
            </button>
            <div className="mt-6 flex flex-col md:flex-row items-center justify-center gap-4 text-[10px] md:text-sm text-blue-300/60 font-bold uppercase tracking-widest" style={{
            gap: "0px"
          }} data-id="element-147" data-mp="2rajwoq2wigs7">
              <span className="flex items-center gap-1.5" data-id="element-148" data-mp="2hbudmtz9qqad">
                <Check className="w-4 h-4 text-green-500" strokeWidth={3} data-id="element-149" data-mp="2hegk0qafazr0" />
                Descarga inmediata
              </span>
              <span className="hidden md:block text-blue-300/20" data-id="element-150" data-mp="3a0rtyrjx6d0h">•</span>
              <span className="flex items-center gap-1.5" data-id="element-151" data-mp="3a2vwb52673yn">
                <Check className="w-4 h-4 text-green-500" strokeWidth={3} data-id="element-152" data-mp="3539jgwq97pqd" />
                Acceso permanente
              </span>
              <span className="hidden md:block text-blue-300/20" data-id="element-153" data-mp="3095ay91cb9n6">•</span>
              <span className="flex items-center gap-1.5" data-id="element-154" data-mp="2vbdk7icfltkk">
                <Check className="w-4 h-4 text-green-500" strokeWidth={3} data-id="element-155" data-mp="3sr9azyve3k60" />
                Sin mensualidades
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* POST-HERO - Antes vs Después Comparison */}
      <section id="post-hero-section" className="py-6 px-4" style={{
      background: '#0a1929'
    }} data-id="element-156" data-mp="5nbjpuv5sua">
        <div className="container mx-auto max-w-2xl" data-id="element-157" data-mp="45xrnvcddzmv">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          type: 'spring',
          stiffness: 100,
          damping: 20
        }} className="relative bg-[#0a1a3a] border-2 border-yellow-500/80 rounded-3xl p-4 md:p-6 shadow-2xl overflow-hidden pt-[10px] pb-[10px] pl-[5px] pr-[5px]" data-id="element-158" data-mp="947mu5y3h08h">
            {/* Comparison Table */}
            <div className="grid grid-cols-2 mb-4 pl-[10px] pr-[10px] gap-[5px]" data-id="element-159" data-mp="wul7hbecnwt1">
              {/* Header */}
              <div className="bg-[#3d1a33] border border-red-500/30 p-2.5 text-center rounded-xl flex items-center justify-center" data-id="element-160" data-mp="11uhdfg6lxyb8">
                <p className="text-[11px] md:text-xs font-black text-pink-300 uppercase tracking-tighter leading-tight" data-id="element-161" data-mp="16qertgyb8uld">
                  HOY <br data-id="element-162" data-mp="1n4d1gtcqs25r" /> TE PASA ESTO
                </p>
              </div>
              <div className="bg-[#1a2b4a] border-2 border-yellow-500/50 p-2.5 text-center rounded-xl flex items-center justify-center" data-id="element-163" data-mp="1n6h4l92tgx4z">
                <p className="text-[11px] md:text-xs font-black text-yellow-400 uppercase tracking-tighter leading-tight" data-id="element-164" data-mp="1w4jfcgccr45g">
                  CON MAESTRO ELÉCTRICO
                </p>
              </div>

              {/* Rows */}
              {[{
              left: 'Mirás un diagrama y no sabés por dónde empezar',
              right: 'Entendés la lógica detrás de cada circuito de mando'
            }, {
              left: 'Conectás componentes, pero no entendés el circuito completo',
              right: 'Interpretás diagramas sin depender de nadie'
            }, {
              left: 'Cuando aparece un tablero, dudás o evitás el trabajo',
              right: 'Armás tableros con criterio técnico'
            }, {
              left: 'Cobras lo mismo hace años',
              right: 'Sabés qué estás haciendo en cada conexión'
            }, {
              left: 'Dependés de otro para validar si está bien',
              right: 'Empezás a encarar trabajos más técnicos y mejor pagos'
            }].map((row, i) => <Fragment key={i} data-id="element-165" data-mp="2scpe5n184lwo" data-mpi={i}>
                  <motion.div initial={{
                opacity: 0,
                x: -10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.1
              }} className="flex items-start gap-2 py-1.5 border-b border-gray-700/30" data-id="element-166" data-mp="2s9jr0dc5nu3l">
                    <X className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-167" data-mp="3b0klop68t3hc" />
                    <p className="text-[11px] md:text-sm text-gray-300 leading-tight font-medium" data-id="element-168" data-mp="1umuf9brni34j">
                      {row.left}
                    </p>
                  </motion.div>
                  <motion.div initial={{
                opacity: 0,
                x: 10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.1
              }} className="flex items-start gap-2 py-1.5 border-b border-gray-700/30 pt-[10px] pb-[10px] pl-[15px] pr-[15px]" data-id="element-169" data-mp="284m3uvrel0o3">
                    <Check className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0 mt-0.5" data-id="element-170" data-mp="1penwyizcddm1" />
                    <p className="text-[11px] md:text-sm text-white leading-tight font-bold" data-id="element-171" data-mp="1udhbmeqd7ans">
                      {row.right}
                    </p>
                  </motion.div>
                </Fragment>)}
            </div>

            {/* Closing statement */}
            <div className="text-center flex flex-col items-center pt-2 gap-[0px]" data-id="element-172" data-mp="16n3islzyh7s5">
              <Zap className="w-6 h-6 text-yellow-400 fill-yellow-400 animate-pulse-glow" data-id="element-173" data-mp="16l0ss1yszenf" />
              <p className="text-sm md:text-base font-black text-yellow-400 leading-tight uppercase tracking-tight" data-id="element-174" data-mp="1bju0fdresbfo">
                UN ELECTRICISTA INDUSTRIAL RESUELVE EL CIRCUITO ANTES DE
                TOCARLO.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* EL PROBLEMA REAL - With animated background */}
      <section id="problem-real-section" className="py-8 px-4 bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900 animate-gradient-slow relative overflow-hidden" data-id="element-175" data-mp="3avdmn6fyourv">
        {/* Subtle pattern */}
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagmonds-light.png')] opacity-[0.02]" data-id="element-176" data-mp="315tzhol7uer7"></div>

        <div className="container mx-auto max-w-3xl relative z-10" data-id="element-177" data-mp="2x2nvknlksznw">
          <motion.h3 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          type: 'spring',
          stiffness: 100,
          damping: 20
        }} className="text-3xl md:text-4xl font-extrabold text-white mb-10 text-center uppercase tracking-wide" data-id="element-178" data-mp="2s7iblubqhv2g">
            EL <span className="text-yellow-400" data-id="element-179" data-mp="2dk1hajvplo6u">PROBLEMA</span> REAL
          </motion.h3>

          {/* Problem Cards - Compact with images */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="flex flex-col items-center gap-4 mb-10" data-id="element-180" data-mp="1zltmoim5ayyu">
            {[{
            image: 'https://cdn.magicpatterns.com/uploads/kTTYbddcoAFGwaZadg6Lz9/imagine_prompt__A_202604230935.webp',
            text: 'Ves un esquema de lógica industrial y no sabés por dónde empezar. Conectás de memoria, rezás para que no vuele nada y, si algo falla,',
            redLine: 'no tenés idea de por qué.'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/jPXCfpqddnTsx86wJZwdCV/_imagine_prompt__Realistic_202604230937.webp',
            text: 'Te llaman para automatizar un tablero o configurar un contactor y tenés que decir que no.',
            redLine: 'Acabás de regalarle ese cliente (y esa plata) a otro que sí sabe.'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/5YfikYVmg6p1jfmJoyEb7c/_imagine_prompt__A_202604230937.webp',
            text: 'Dependés de que un tercero valide lo que hacés. Sin la lógica clara, quedás expuesto ante el cliente y',
            redLine: 'perdés la autoridad como profesional.'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/xhRSvJF7qdoXAjziMfNMU9/_imagine_prompt__Realistic_202604230937_(1).webp',
            text: 'Seguís cobrando lo mismo de hace años haciendo instalaciones básicas. Sin dominar el control industrial, tu techo ya está puesto y',
            redLine: 'estás dejando pasar los trabajos que realmente valen.'
          }].map((card, i) => <motion.div key={i} variants={itemVariants} whileHover={{
            scale: 1.02
          }} className="w-full max-w-[600px] bg-white rounded-xl overflow-hidden shadow-lg transition-all duration-300" data-id="element-181" data-mp="261twzz120cii" data-mpi={i}>
                <div className="grid grid-cols-[100px_1fr] md:grid-cols-[140px_1fr] min-h-[120px]" data-id="element-182" data-mp="filyescy2iox">
                  <div className="aspect-[3/4] overflow-hidden" data-id="element-183" data-mp="jk77e2mcyctu">
                    <img src={card.image} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" width={140} height={187} data-id="element-184" data-mp="jiw21yd9kx0m" />
                  </div>
                  <div className="p-3 md:p-4 flex flex-col justify-center gap-1" data-id="element-185" data-mp="thbvi7zrkida">
                    <p className="text-gray-700 text-xs md:text-sm leading-snug font-[500]" data-id="element-186" data-mp="ygy5m8kapihv">
                      {card.text}
                    </p>
                    <p className="text-red-600 text-xs md:text-sm font-bold leading-snug" data-id="element-187" data-mp="138zpafjsmbhx">
                      {card.redLine}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ¿TE PASA ESTO? - With animated background */}
      <section id="fears-section" className="py-8 px-4 bg-gradient-to-br from-gray-950 via-blue-950 to-gray-950 animate-gradient-slow relative overflow-hidden" data-id="element-188" data-mp="1rbw9it7nx70p">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagmonds-light.png')] opacity-[0.02]" data-id="element-189" data-mp="1mbi29ddvy13q"></div>

        <div className="container mx-auto max-w-3xl relative z-10" data-id="element-190" data-mp="1dcehm4z2ldn4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-191" data-mp="1de7sjwgpuk0x">
            <h3 className="md:text-5xl font-extrabold text-white uppercase text-[32px] mb-[0px]" data-id="element-192" data-mp="20ckphtgm9hg8">
              ¿TE <span className="text-yellow-400" data-id="element-193" data-mp="1wazaa1gm0r2v">PASA</span> ESTO?
            </h3>
            <p className="text-gray-400 text-[18px] pb-[20px] mt-[0px] mb-[0px]" data-id="element-194" data-mp="1w96a950i553u">
              Si trabajás con tableros, motores y circuitos, esto es para vos.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          type: 'spring',
          stiffness: 100,
          damping: 20,
          delay: 0.2
        }} className="problem-card bg-gray-800/80 backdrop-blur-sm rounded-2xl border-l-4 border-red-500 p-6 md:p-8 shadow-lg pt-[10px] pb-[10px] pr-[24px] space-y-[1px]" data-id="element-195" data-mp="2o0c3r2lpw4k5">
            <div className="space-y-6" data-id="element-196" data-mp="3ck7gx7rxbfz">
              {fearItems.map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.4 + i * 0.1
            }} className="flex items-start gap-4 pb-6 border-b border-gray-700 last:border-0 pb-[10px] mt-[10px] gap-[16px] items-center justify-center" data-id="element-197" data-mp="8dhxfvz0z9s1" data-mpi={i}>
                  <div className="w-9 h-9 bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-198" data-mp="1ssa3hrbg4o7c">
                    <item.icon className="w-4 h-4 text-red-400" data-id="element-199" data-mp="3d8re1z2rpjye" />
                  </div>
                  <div data-id="element-200" data-mp="389y24b34q10c">
                    <h4 className="font-bold text-white text-lg mb-1" data-id="element-201" data-mp="3n0nehgsbtm5s">
                      {item.title}
                    </h4>
                    <p className="text-gray-400" data-id="element-202" data-mp="3i95otovr7804">{item.desc}</p>
                  </div>
                </motion.div>)}
            </div>

            <div className="border-t border-gray-700 text-center mt-[30px] pt-[35px] pb-[35px]" data-id="element-203" data-mp="3vzsekrjjdnk2">
              <p className="text-xl font-medium text-gray-300 mb-2" data-id="element-204" data-mp="3rzqm98zoy6hg">
                Ahí aparece la inseguridad.
              </p>
              <p className="text-xl font-bold text-white" data-id="element-205" data-mp="4vaogmw69t6j">
                Y en control eléctrico, la inseguridad{' '}
                <span className="text-yellow-400" data-id="element-206" data-mp="2bl8lsp75hggh">se paga caro</span>.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* BENEFITS GRID SECTION - COMPRESSED */}
      <section id="benefits-grid" className="py-10 md:py-16 bg-white relative overflow-hidden" data-id="element-207" data-mp="1ku9qx9g9m8ay">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagmonds-light.png')] opacity-[0.03]" data-id="element-208" data-mp="1prt406188f1b"></div>

        <div className="container mx-auto px-4 relative z-10" data-id="element-209" data-mp="1uk30k4wukdck">
          <div className="text-center mb-8 md:mb-12" data-id="element-210" data-mp="1zintrkdolwri">
            <h2 className="text-2xl md:text-4xl font-black text-gray-900 uppercase tracking-tight" data-id="element-211" data-mp="1zk8k0xsyqtjk">
              Con esta guía <span className="text-blue-600" data-id="element-212" data-mp="16tgtafuo2x3m">vas a poder:</span>
            </h2>
            <div className="w-16 h-1 bg-blue-600 mx-auto mt-4 rounded-full" data-id="element-213" data-mp="16vjsnqhpm2l0"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 max-w-7xl mx-auto" data-id="element-214" data-mp="1bv66a11g9kuc">
            {[{
            title: 'Leer e interpretar cualquier diagrama',
            desc: 'Aprenderás cada símbolo y conexión con ilustraciones 100% visuales.',
            icon: Ruler,
            highlight: true
          }, {
            title: 'Instalar 20 circuitos paso a paso',
            desc: '9 monofásicos + 11 trifásicos: arranque directo, inversión, estrella-triángulo y más.',
            icon: Zap
          }, {
            title: 'Diagnosticar las 16 fallas más comunes',
            desc: 'Capítulo exclusivo con causa y solución paso a paso para fallas reales.',
            icon: Search
          }, {
            title: 'Trabajar con seguridad total — LOTO',
            desc: 'Procedimientos exactos (OSHA/NOM) para intervenir equipos sin riesgo.',
            icon: Lock
          }, {
            title: 'Dimensionar sin calcular de memoria',
            desc: 'Tablas listas: HP → FLA → cable → protección. Selecciona en segundos.',
            icon: BarChart3
          }, {
            title: 'Saber cuánto cobrar sin dudar',
            desc: 'Tabla de presupuesto incluida para calcular precios de forma rápida y segura.',
            icon: Banknote
          }, {
            title: 'Dominar NEC, NOM e IEC',
            desc: 'Diferencias entre normas para trabajar en cualquier país con idioma técnico.',
            icon: Globe,
            isNew: true,
            highlight: true
          }, {
            title: 'Glosario español-inglés',
            desc: 'Más de 200 términos técnicos. Ideal para trabajar con equipos importados.',
            icon: BookOpen,
            isNew: true,
            highlight: true
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.05
          }} className={`relative bg-white p-5 rounded-2xl border-2 transition-all duration-300 flex flex-col h-full shadow-sm hover:shadow-md ${item.highlight ? 'border-blue-500/30' : 'border-gray-100'}`} data-id="element-215" data-mp="1mevawe016cqs" data-mpi={i}>
                {item.isNew && <div className="absolute -top-2.5 left-4 bg-blue-600 text-white text-[8px] font-black px-2 py-0.5 rounded-full flex items-center gap-1 shadow-md z-10" data-id="element-216" data-mp="31vlvjhrjzfrg">
                    ★ NOVEDAD
                  </div>}

                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center mb-4" data-id="element-217" data-mp="3eatpeni9ocbn">
                  <item.icon className="w-5 h-5 text-blue-600" data-id="element-218" data-mp="3j5zaxl7rboz7" />
                </div>

                <h3 className="text-sm font-black text-gray-900 mb-2 leading-tight" data-id="element-219" data-mp="3o1y8nzez6jca">
                  {item.title}
                </h3>

                <p className="text-gray-500 leading-snug font-medium text-[12px]" data-id="element-220" data-mp="2vdsi2w18q04j">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section id="solution" className="bg-white" data-id="element-221" data-mp="35d1b9moa7apu">
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="container mx-auto max-w-4xl px-4 py-16 text-center bg-[#091D47] pt-[15px] pb-[15px] pl-[16px] pr-[16px]" data-id="element-222" data-mp="fnpi0ww0k2mr">
          <motion.span initial={{
          y: 10,
          opacity: 0
        }} whileInView={{
          y: 0,
          opacity: 1
        }} viewport={{
          once: true
        }} className="bg-yellow-400 text-blue-900 font-black px-4 py-1.5 rounded-full text-xs uppercase tracking-widest mb-6 inline-block shadow-sm" data-id="element-223" data-mp="3r52ph73oqpz1">
            LA SOLUCIÓN DEFINITIVA
          </motion.span>
          <motion.h2 initial={{
          y: 20,
          opacity: 0
        }} whileInView={{
          y: 0,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-4xl md:text-5xl lg:text-6xl font-black mb-4 uppercase tracking-tight" data-id="element-224" data-mp="38anoxd1q6p93">
            <span className="text-white" data-id="element-225" data-mp="2pmaa8j8ia91q">AHÍ NACE </span>
            <span className="text-yellow-400" data-id="element-226" data-mp="2pkphmz5r506i">MAESTRO ELÉCTRICO</span>
          </motion.h2>
          <motion.p initial={{
          y: 20,
          opacity: 0
        }} whileInView={{
          y: 0,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3
        }} className="text-xl text-gray-400 font-medium" data-id="element-227" data-mp="2kme70m7muln8">
            Un manual práctico creado para ayudarte a:
          </motion.p>
        </motion.div>

        {/* Two-Column Block */}
        <div className="bg-[#091D47] py-20 pt-[20px] pb-[20px]" data-id="element-228" data-mp="v6lfp5fy2kel">
          <div className="container mx-auto max-w-6xl px-4" data-id="element-229" data-mp="1ivczp9jdrwga">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center" data-id="element-230" data-mp="1itted4ivav7n">
              {/* Left Column - Text */}
              <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} className="w-full space-y-3" data-id="element-231" data-mp="19vhh4jnhwz68">
                {['Entender la lógica de los circuitos de mando.', 'Leer diagramas con seguridad.', 'Aplicar lo aprendido en motores y tableros reales.', 'Dejar de memorizar esquemas y empezar a comprenderlos.'].map((item, index) => <motion.div key={index} variants={itemVariants} className="flex items-start gap-3 p-3 rounded-lg" data-id="element-232" data-mp="io98lkqi4xxx" data-mpi={index}>
                    <div className="bg-yellow-400 p-1 rounded-full flex-shrink-0 mt-0.5" data-id="element-233" data-mp="dxiyxvb1m8xf">
                      <Check className="w-3.5 h-3.5 text-blue-900 stroke-[3px]" data-id="element-234" data-mp="8y6chbo333vd" />
                    </div>
                    <p className="text-blue-100 font-medium text-base leading-snug" data-id="element-235" data-mp="40eeq10rcnj9">
                      {item}
                    </p>
                  </motion.div>)}
              </motion.div>

              {/* Right Column - Horizontal Carousel */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              type: 'spring',
              stiffness: 100,
              damping: 20,
              delay: 0.4
            }} className="w-full" data-id="element-236" data-mp="wm7m6nx00qcd">
                <HorizontalCarousel images={['https://cdn.magicpatterns.com/uploads/9REeJugtPE4hwPnc79PgSY/Una_foto_fotorrealista_202604171241.webp', 'https://cdn.magicpatterns.com/uploads/7CHbsG1wjqAEbXPkMdWWtf/Hyper-realistic,_intimate_phone_202604151004.webp', 'https://cdn.magicpatterns.com/uploads/urm1EM6Ho19epW6AzKFAHc/Photorealistic,_intimate_medium_202604151012.webp', 'https://cdn.magicpatterns.com/uploads/ocqY51CbjtDLpjCtARHT4W/Hyper-realistic,_intimate_phone_202604151010.webp', 'https://cdn.magicpatterns.com/uploads/uR18KAMY2DX3wu2XqJbQph/Hyper-realistic,_candid_selfie_202604150954.webp', 'https://cdn.magicpatterns.com/uploads/dDcLT75SACqVQUA8UnAgUK/Hyper-realistic,_documentary-style_photograph._202604151012.webp']} data-id="element-237" data-mp="38fxp937ar8lg" />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* DECISION BLOCK */}
      <section className="py-8 px-4 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white relative overflow-hidden" data-id="element-238" data-mp="la0gg8nbljp0">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10" data-id="element-239" data-mp="q8adkosluud9"></div>

        {/* Professional Work Headline + Marquee */}
        <div className="relative z-10 pt-[0px] pb-[32px]" data-id="element-240" data-mp="1olfm0yw8jpq1">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="md:text-5xl font-extrabold text-center uppercase px-4 text-[18px] pl-[16px] pr-[16px] mb-[15px]" data-id="element-241" data-mp="22k5jifmyvrex">
            LOS <span className="text-[#FFD700]" data-id="element-242" data-mp="27j8ra7uc0bql">TRABAJOS PROFESIONALES</span>{' '}
            QUE VAS A PODER LOGRAR
          </motion.h2>

          {/* Seamless Marquee */}
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1
        }} className="w-full overflow-hidden" style={{
          maskImage: 'linear-gradient(to right, transparent, black 15%, black 85%, transparent)',
          WebkitMaskImage: 'linear-gradient(to right, transparent, black 15%, black 85%, transparent)'
        }} data-id="element-243" data-mp="2lbybq7u7p8ll">
            <div className="work-marquee-track flex" style={{
            gap: 0
          }} data-id="element-244" data-mp="3ess2ah3fadyb">
              {[...Array(2)].map((_, setIdx) => <div key={setIdx} className="flex" style={{
              gap: 0
            }} data-id="element-245" data-mp="2zz6srpa7c85h" data-mpi={setIdx}>
                  {['https://cdn.magicpatterns.com/uploads/7HGnWVYdGUounHGPTR5os9/ANXO_Cidery2-884x936.webp', 'https://cdn.magicpatterns.com/uploads/1dL5zYkw5FFehBYAgx2tnp/IMG_20220326_113419434_HDR.webp', 'https://cdn.magicpatterns.com/uploads/9QDkoYsbeL9saJPGxUxeGM/l.webp', 'https://cdn.magicpatterns.com/uploads/c2yC3nXaAGLmMwdQdVXgAk/4adca5c332d9c89ba2eb667fbec01413.webp', 'https://cdn.magicpatterns.com/uploads/w3LHvz1VbrGMohvvkY3fc8/02_(1).webp', 'https://cdn.magicpatterns.com/uploads/ov65eS7vV5W5DJn5gyZ4Gi/capture_260423_102902.webp', 'https://cdn.magicpatterns.com/uploads/47tYGnACbZUj4Jiak4CcvX/Electrician_smiling_at_202604231027.webp', 'https://cdn.magicpatterns.com/uploads/hPrxHuZ14satPewQLK9Jus/Electrician_using_multimeter_202604231026_(1).webp', 'https://cdn.magicpatterns.com/uploads/wjbtDSssEcZSkMbHzQHbDQ/650301318_865258023230524_1857744393700602518_n.webp', 'https://cdn.magicpatterns.com/uploads/jm6cXtUVxQEbLdjG1CoaRA/descargar.webp'].map((src, i) => <div key={`${setIdx}-${i}`} className="flex-shrink-0" style={{
                width: '220px',
                aspectRatio: '3/4'
              }} data-id="element-246" data-mp="2doglhg9bxn4r" data-mpi={i}>
                      <img src={src} alt={`Trabajo profesional ${i + 1}`} className="w-full h-full object-cover" loading={i < 3 ? 'eager' : 'lazy'} decoding="async" width={220} height={293} data-id="element-247" data-mp="24xg52by983oc" />
                    </div>)}
                </div>)}
            </div>
          </motion.div>
        </div>

        <div className="container mx-auto max-w-4xl relative z-10" data-id="element-248" data-mp="1pznuzzbh87hj">
          <div className="max-w-3xl mx-auto mb-12" data-id="element-249" data-mp="1q209g2i0zzpu">
            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="space-y-3" data-id="element-250" data-mp="1ly2b3u0iysxu">
              {decisionItems.map((item, index) => <motion.div key={index} variants={itemVariants} whileHover={{
              scale: 1.02,
              x: 5
            }} className="flex items-center gap-4 p-4 rounded-lg bg-blue-800/40 border border-blue-600/40 transition-all duration-300 hover:bg-blue-700/50 hover:border-blue-500/50 hover:shadow-lg" data-id="element-251" data-mp="5h3jwdldm07i" data-mpi={index}>
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" data-id="element-252" data-mp="3dz9gt15thdzi">
                    <item.icon className="w-5 h-5 text-yellow-400" data-id="element-253" data-mp="3ixjcdcvgegln" />
                  </div>
                  <span className="text-lg text-gray-100" data-id="element-254" data-mp="3nut8fbdclqig">
                    {item.text.split(item.keyword).map((part, i, arr) => <span key={i} data-id="element-255" data-mp="3so5m4qghocwf" data-mpi={i}>
                        {part}
                        {i < arr.length - 1 && <span className="text-blue-300" data-id="element-256" data-mp="2v6dwixpijobc">{item.keyword}</span>}
                      </span>)}
                  </span>
                </motion.div>)}
            </motion.div>
          </div>

          <div className="text-center" data-id="element-257" data-mp="29xnz6db51w4t">
            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={scrollToPurchase} className="btn-shine bg-yellow-500 hover:bg-yellow-400 text-blue-900 text-2xl font-extrabold py-5 px-12 rounded-lg shadow-2xl hover:shadow-yellow-500/30 transition-all" data-id="element-258" data-mp="29ujnyvz9a04n">
              SÍ, QUIERO LA OFERTA AHORA
            </motion.button>
          </div>
        </div>
      </section>

      {/* BONUSES - Carousel */}
      <section id="bonuses" className="py-8 px-4 bg-[#00005E]" data-id="element-259" data-mp="1vy62lbrlpekw">
        <div className="container mx-auto max-w-6xl" data-id="element-260" data-mp="1h8go6euhze2o">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-261" data-mp="1h5m6uw6s69dh">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 tracking-tight relative inline-block" data-id="element-262" data-mp="1d63skj9gswjm">
              SI ACCEDÉS HOY, TE LLEVÁS ESTOS 5 BONUS DE REGALO
              <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent rounded-full" data-id="element-263" data-mp="tmeipubbyugb"></span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-6 mt-4" data-id="element-264" data-mp="12mbc298o0hhp">
              Complementos perfectos para tu crecimiento profesional
            </p>

            <BookStackFan data-id="element-265" data-mp="1ci4j5d3m7is3" />

            {/* Swipe Instruction - High Visibility for older users */}
            <div className="mb-6 flex flex-col items-center gap-3" data-id="element-266" data-mp="17j1pspwzz0zy">
              <motion.p animate={{
              x: [0, 5, 0]
            }} transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="text-yellow-400 font-black text-sm md:text-base tracking-widest uppercase flex items-center gap-2" data-id="element-267" data-mp="1m8prvtd063oq">
                DESLIZÁ PARA VER TODOS LOS BONOS INCLUIDOS
                <ArrowRight className="w-5 h-5" data-id="element-268" data-mp="1q8hrvhobz1ih" />
              </motion.p>

              {/* Animated Swipe Indicator */}
              <div className="flex items-center gap-4 text-gray-500 md:hidden" data-id="element-269" data-mp="sw9ap01fcwj9">
                <motion.div animate={{
                x: [-10, 10, -10],
                opacity: [0.3, 1, 0.3]
              }} transition={{
                duration: 2,
                repeat: Infinity
              }} data-id="element-270" data-mp="su53o988bher">
                  <ChevronLeft className="w-6 h-6" data-id="element-271" data-mp="16nwgbqh9bhep" />
                </motion.div>
                <div className="w-12 h-1 bg-gray-800 rounded-full overflow-hidden" data-id="element-272" data-mp="16q0h3zjv00ar">
                  <motion.div animate={{
                  x: [-48, 48, -48]
                }} transition={{
                  duration: 2,
                  repeat: Infinity
                }} className="w-full h-full bg-yellow-500/50" data-id="element-273" data-mp="1botza5a3cfh9" />
                </div>
                <motion.div animate={{
                x: [10, -10, 10],
                opacity: [0.3, 1, 0.3]
              }} transition={{
                duration: 2,
                repeat: Infinity
              }} data-id="element-274" data-mp="3ie8il1yjhyig">
                  <ChevronRight className="w-6 h-6" data-id="element-275" data-mp="3ewucujvfspac" />
                </motion.div>
              </div>
            </div>

            {/* Snap-Scroll Carousel */}
            <div className="relative group" data-id="element-276" data-mp="354ojp6tg81tk">
              <div ref={bonusScrollRef} onScroll={handleBonusScroll} className="flex gap-4 md:gap-8 overflow-x-auto snap-x snap-mandatory hide-scrollbar pb-8 px-4 md:px-0" style={{
              WebkitOverflowScrolling: 'touch'
            }} data-id="element-277" data-mp="355pv5cedmqbt">
                {[{
                title: 'CURSO DE PANELES SOLARES',
                desc: 'Más de 50 clases grabadas para sumar una nueva especialidad altamente demandada en energía solar.',
                img: 'https://cdn.magicpatterns.com/uploads/w2rTPZ1b9h5D7JWz5dRRda/usa-la-imagen-adjunta-como-referencia-vi_LIZOyjwkQ0eu3ZbUzqbGFw_459-daMfTbW7XMmyjjHVYA_2k.png',
                value: '$32.500 ARS',
                exclusive: true,
                label: 'BONO EXCLUSIVO'
              }, {
                title: 'PLANTILLAS DE PRESUPUESTO EDITABLE',
                desc: 'Herramienta editable diseñada para trabajos eléctricos profesionales.',
                img: 'https://cdn.magicpatterns.com/uploads/52EEtPjWC34BYh7Nk8wekR/Prompt__A_high-end,_202604151035.webp',
                value: '$6.990 ARS',
                exclusive: false,
                label: 'HOY: GRATIS'
              }, {
                title: 'PROTOCOLO DE VERIFICACIÓN PREVIA A LA ENERGIZACIÓN',
                desc: 'Documento técnico estructurado que establece criterios de verificación.',
                img: 'https://cdn.magicpatterns.com/uploads/ecDoD4jMmyVG3yjoo5c8Tn/Prompt__A_detailed,_202604151040.webp',
                value: '$9.990 ARS',
                exclusive: false,
                label: 'HOY: GRATIS'
              }, {
                title: 'EFICIENCIA EN USO INDUSTRIAL DE MOTORES ELÉCTRICOS',
                desc: 'Material técnico orientado al uso correcto de motores eléctricos en entornos industriales.',
                img: 'https://cdn.magicpatterns.com/uploads/nH726tHAQweq2rPqiPh3zm/A_detailed,_premium_202604151030.webp',
                value: '$14.990 ARS',
                exclusive: false,
                label: 'HOY: GRATIS'
              }, {
                title: 'REGLAMENTACIONES AEA - ASOCIACIÓN ELECTROTÉCNICA ARGENTINA',
                desc: 'Manual esencial sobre las normativas vigentes de la AEA para instalaciones eléctricas seguras y profesionales.',
                img: 'https://cdn.magicpatterns.com/uploads/xz24GCFVSGY7FVuNDfknEw/necesito_hacer_el_202604151258.jpg',
                value: '$12.500 ARS',
                exclusive: false,
                label: 'HOY: GRATIS'
              }].map((bonus, i) => <div key={i} className="flex-shrink-0 w-[85%] md:w-[320px] snap-center md:snap-start" data-id="element-278" data-mp="2dbmeloiektpv" data-mpi={i}>
                    <motion.div whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} onClick={scrollToPurchase} className="cursor-pointer h-full" data-id="element-279" data-mp="1pls7zooog1e2">
                      <div className={`bg-gray-950 rounded-xl overflow-hidden border-2 h-full flex flex-col ${bonus.exclusive ? 'border-yellow-400 shadow-xl shadow-yellow-500/10' : 'border-yellow-600/50 shadow-lg'}`} data-id="element-280" data-mp="3koyjybu1uxyj">
                        <div className={`${bonus.exclusive ? 'bg-gradient-to-r from-yellow-600 to-yellow-500 text-black' : 'bg-gradient-to-r from-yellow-700 to-yellow-600 text-black'} px-4 py-2 text-center font-bold text-sm uppercase`} data-id="element-281" data-mp="2cjytcrm2de6">
                          {bonus.label}
                        </div>
                        <div className="p-5 flex flex-col flex-grow" data-id="element-282" data-mp="3as3ndyhc77mw">
                          <div className="aspect-[3/4] mb-4 bg-black rounded-lg overflow-hidden relative" data-id="element-283" data-mp="3auy8ls86cwhd">
                            {bonus.exclusive && <div className="absolute top-2 left-2 bg-yellow-500 text-black text-[10px] font-bold uppercase px-2 py-1 rounded z-10 tracking-wider" data-id="element-284" data-mp="q2w5ylf1863p">
                                BONO PREMIUM
                              </div>}
                            <img src={bonus.img} alt={bonus.title} className="w-full h-full object-cover" loading="lazy" decoding="async" width={300} height={400} data-id="element-285" data-mp="154mh3b2l0t39" />
                          </div>
                          <h3 className={`font-bold text-sm ${bonus.exclusive ? 'text-yellow-400' : 'text-white'} mb-3 leading-tight uppercase`} data-id="element-286" data-mp="ve1i3mizxmjt">
                            {bonus.title}
                          </h3>
                          <p className="text-sm text-gray-400 mb-4 leading-relaxed flex-grow" data-id="element-287" data-mp="mctqfnazjjyp">
                            {bonus.desc}
                          </p>
                          <p className="text-sm text-gray-500 mb-2" data-id="element-288" data-mp="chi8yx86zyn9">
                            Valor:{' '}
                            <span className={`line-through ${bonus.exclusive ? 'text-red-400 font-medium' : 'text-gray-500'}`} data-id="element-289" data-mp="8gpxryfrfjoc">
                              {bonus.value}
                            </span>
                          </p>
                          <div className={`${bonus.exclusive ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-green-500/10 border-green-500/30'} border rounded-lg px-3 py-2 mt-auto`} data-id="element-290" data-mp="2m1wepnrgtb5r">
                            <p className={`text-sm font-bold ${bonus.exclusive ? 'text-yellow-400' : 'text-green-400'}`} data-id="element-291" data-mp="2q5m4ggjvlysd">
                              ✓ Incluido sin costo adicional
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </div>)}
              </div>

              {/* Pagination Dots */}
              <div className="flex justify-center gap-2 mt-2" data-id="element-292" data-mp="1td8pxi7ic3ng">
                {[...Array(5)].map((_, i) => <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === activeBonusIndex ? 'bg-yellow-500 w-6' : 'bg-gray-700 w-1.5'}`} data-id="element-293" data-mp="1tflcy9s4h47d" data-mpi={i} />)}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* EXTRA BONUS - CERTIFICATE */}
      <section className="py-8 px-4 bg-gradient-to-b from-[#0a1a3a] to-[#061229] relative overflow-hidden" data-id="element-294" data-mp="3so0dfesxgukk">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagmonds-light.png')] opacity-[0.02]" data-id="element-295" data-mp="1pst2yn324uw"></div>
        <div className="container mx-auto max-w-2xl relative z-10" data-id="element-296" data-mp="fi90ebn0op1m">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          type: 'spring',
          stiffness: 100,
          damping: 20
        }} className="flex flex-col items-center text-center" data-id="element-297" data-mp="aqqz1mh30mg4">
            {/* Certificate Image at the top with frame */}
            <motion.div whileHover={{
            scale: 1.05,
            rotate: 1
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 15
          }} className="relative group w-full max-w-[320px] mb-6" data-id="element-298" data-mp="uerd61arnh2u">
              <div className="absolute -inset-1 bg-blue-500/10 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-700" data-id="element-299" data-mp="2w7nvm9709lwz"></div>
              <div className="relative bg-[#1a2b4a] border-[6px] border-[#2a3b5a] p-2 md:p-4 rounded-xl shadow-2xl overflow-hidden" data-id="element-300" data-mp="37kczkuu58ymb">
                <img src="https://cdn.magicpatterns.com/uploads/wgzBvYCj2MeubudywG2qNn/Diseno_sin_titulo_(2).png" alt="Certificado Maestro Eléctrico" className="w-full h-auto rounded-sm" loading="lazy" decoding="async" width={320} height={240} data-id="element-301" data-mp="3lcjdsbdqkvj4" />
              </div>
            </motion.div>

            {/* Lifetime Access Badge */}
            <motion.div initial={{
            scale: 0.9,
            opacity: 0
          }} whileInView={{
            scale: 1,
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} data-id="element-302" data-mp="3qce9qtcdcbb1">
              <span className="bg-green-500/20 text-green-400 border border-green-500/30 font-black px-3 py-1 rounded-full text-[10px] uppercase tracking-widest shadow-lg" data-id="element-303" data-mp="29wbj0w4jqoi0">
                ACCESO VITALICIO
              </span>
            </motion.div>

            {/* Badge */}
            <span className="bg-yellow-500 text-blue-900 font-black px-3 py-1 rounded text-[10px] uppercase tracking-widest mb-4 shadow-lg" data-id="element-304" data-mp="pcpoahjd6men">
              BONO ESPECIAL
            </span>

            {/* Title */}
            <h2 className="text-xl md:text-3xl font-extrabold text-white mb-3 leading-tight uppercase tracking-tight" data-id="element-305" data-mp="fmeisqk1855n">
              Certificado Profesional en <br className="hidden md:block" data-id="element-306" data-mp="6mzu9wnfe4ns" />
              Comandos Eléctricos
            </h2>

            {/* Description */}
            <p className="text-xs md:text-sm text-blue-100 max-w-xl mb-6 leading-relaxed opacity-80" data-id="element-307" data-mp="3otxqjdf9b50q">
              Al completar la guía, recibís un certificado digital de alta
              calidad listo para compartir con tus clientes y en redes sociales.
              Incluye una guía de una página sobre cómo usarlo para generar
              confianza y conseguir más trabajos.
            </p>

            {/* Included Gratis Badge */}
            <motion.div whileHover={{
            scale: 1.05
          }} className="inline-flex items-center gap-2 bg-blue-900/40 border border-blue-400/30 px-4 py-1.5 rounded-full" data-id="element-308" data-mp="2zxyna16l8r3j">
              <Check className="w-3.5 h-3.5 text-blue-400 stroke-[3px]" data-id="element-309" data-mp="2qzn0ka8uedgd" />
              <span className="text-blue-100 font-bold uppercase tracking-wider text-[10px]" data-id="element-310" data-mp="2m1bmfnbilgsc">
                Incluido Gratis con tu compra
              </span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* LO QUE TE LLEVÁS */}
      <section className="py-16 px-4 bg-[#0B1530] relative overflow-hidden" data-id="element-311" data-mp="39xzahy7kvrd0">
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'linear-gradient(rgba(59,130,246,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.3) 1px, transparent 1px)',
        backgroundSize: '40px 40px'
      }} data-id="element-312" data-mp="34zxr1c68pq10"></div>

        <div className="container mx-auto max-w-5xl relative z-10" data-id="element-313" data-mp="8tk7cn36xrpo">
          <motion.h2 initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="md:text-4xl text-white mb-12 text-[28px] font-[700] font-[Poppins,_sans-serif] uppercase tracking-tight" data-id="element-314" data-mp="8veqpwytdbrn">
            LO QUE TE LLEVÁS CON MAESTRO ELÉCTRICO
          </motion.h2>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-315" data-mp="rrn27iif6ive">
            {[{
            image: 'https://cdn.magicpatterns.com/uploads/wZLFDJarxjC6YR14WGgqpR/Electrician_pointing_multimeter_202604231237.webp',
            title: 'CRITERIO Y SEGURIDAD',
            items: ['Más seguridad frente a los diagramas', 'Más criterio técnico'],
            tag: 'BASE TÉCNICA'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/vC9EYvMoTBEfW2EdR9JNKT/Industrial_control_panel_202604231237.webp',
            title: 'MENOS ERRORES',
            items: ['Menos errores', 'Menos dudas'],
            tag: 'MENOS RETRABAJO'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/gGvG1gp9LNad9JgT5vsQqK/Electrician_shaking_hands_202604231237.webp',
            title: 'PERFIL PROFESIONAL',
            items: ['Más confianza profesional', 'Mejor imagen ante el cliente'],
            tag: 'MEJOR IMAGEN'
          }].map((card, i) => <motion.div key={i} variants={itemVariants} whileHover={{
            y: -5
          }} className="bg-[#162447] border border-blue-500/20 rounded-2xl overflow-hidden shadow-2xl flex flex-col h-full" data-id="element-316" data-mp="evm41ncdkqvc" data-mpi={i}>
                {/* Image Area (40%) */}
                <div className="relative h-48 overflow-hidden" data-id="element-317" data-mp="nxljwcbnlowp">
                  <img src={card.image} alt={card.title} className="w-full h-full object-cover" loading="lazy" decoding="async" width={400} height={192} data-id="element-318" data-mp="248h6o0f238h5" />
                  {/* Gradient Overlay & Inner Shadow */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#162447] via-transparent to-black/20" data-id="element-319" data-mp="3h6r9rbjew997"></div>
                </div>

                {/* Content Area */}
                <div className="p-6 flex flex-col flex-1" data-id="element-320" data-mp="388nwf2ikyish">
                  <h3 className="font-bold text-white text-lg leading-tight tracking-tight mb-4 font-[Poppins,_sans-serif] uppercase" data-id="element-321" data-mp="3d6pbzdgsrzz7">
                    {card.title}
                  </h3>

                  <div className="space-y-3 mb-8 flex-1" data-id="element-322" data-mp="8shihcspsj62">
                    {card.items.map((item, j) => <div key={j} className="flex items-start gap-3" data-id="element-323" data-mp="8ubsvx6d0svw" data-mpi={j}>
                        <Check className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0 stroke-[3px]" data-id="element-324" data-mp="3r5yuwkwe186v" />
                        <span className="text-gray-300 text-sm leading-snug" data-id="element-325" data-mp="3w40dz5ttd7ic">
                          {item}
                        </span>
                      </div>)}
                  </div>

                  {/* Badge at bottom */}
                  <div className="mt-auto" data-id="element-326" data-mp="12cgo08guba0s">
                    <span className="inline-block bg-blue-600/20 text-blue-400 text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest border border-blue-500/30 shadow-[0_0_10px_rgba(59,130,246,0.2)]" data-id="element-327" data-mp="12bf9tv4j27eu">
                      {card.tag}
                    </span>
                  </div>
                </div>
              </motion.div>)}
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.5
        }} className="mt-16 bg-[#0f2044] border border-blue-800/40 text-white p-8 md:p-10 rounded-2xl text-center shadow-2xl" data-id="element-328" data-mp="je4eumpxi057">
            <p className="text-lg md:text-xl font-semibold leading-relaxed font-[Poppins,_sans-serif] max-w-3xl mx-auto" data-id="element-329" data-mp="4mlrg3uqp3bt">
              Invertís una vez en criterio. Eso se traduce en más trabajos,
              mejores decisiones y clientes que confían en vos.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SOCIAL PROOF CAROUSEL */}
      <section className="py-12 px-4 bg-gray-100 overflow-hidden" data-id="element-330" data-mp="2bbxeobncltuo">
        <div className="container mx-auto max-w-6xl" data-id="element-331" data-mp="2gcbitiltwjnf">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-10" data-id="element-332" data-mp="2gcux8pc9spwp">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4 font-[Poppins,_sans-serif]" data-id="element-333" data-mp="1nljo6pkew327">
              LO QUE DICEN OTROS PROFESIONALES
            </h2>
            <p className="text-gray-600" data-id="element-334" data-mp="1xjzif1eqjsfx">
              Más de 6.250 electricistas ya están aplicando este criterio
              técnico
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1
        }} className="relative" data-id="element-335" data-mp="14vva084s519j">
            <div className="flex gap-6 overflow-x-auto pb-8 snap-x snap-mandatory hide-scrollbar" data-id="element-336" data-mp="38xdnlu5t6ga">
              {testimonialData.map((t, i) => <motion.div key={i} whileHover={{
              y: -5
            }} className="flex-shrink-0 w-[300px] md:w-[400px] bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden snap-center flex flex-col transition-all duration-300" data-id="element-337" data-mp="c9c1osyicznh" data-mpi={i}>
                  <div className="aspect-square overflow-hidden relative" data-id="element-338" data-mp="m5ykzdb6rine">
                    <img src={t.image} alt={t.name} className="w-full h-full object-cover" loading="lazy" decoding="async" width={1080} height={1080} data-id="element-339" data-mp="h7dj79gacqwu" />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent p-4" data-id="element-340" data-mp="v1nyz1ab1aqr">
                      <p className="text-white font-bold text-lg" data-id="element-341" data-mp="14x7uhmkyi7ez">{t.name}</p>
                      <p className="text-blue-300 text-sm font-medium" data-id="element-342" data-mp="zz6bt2rg0a41">
                        {t.study}
                      </p>
                    </div>
                  </div>
                  <div className="p-6 flex-grow flex flex-col" data-id="element-343" data-mp="1jivff9yzhfyw">
                    <div className="flex items-center gap-0.5 mb-4" data-id="element-344" data-mp="1oihvrsa3zcbx">
                      {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-345" data-mp="19lqsh7xbdgh1" />)}
                    </div>
                    <p className="text-gray-700 italic mb-6 flex-grow" data-id="element-346" data-mp="1ejs9zof3d7qe">
                      "{t.comment}"
                    </p>
                    <div className="pt-4 border-t border-gray-100" data-id="element-347" data-mp="15kyi2bcph5j6">
                      <p className="text-xs text-gray-400 uppercase tracking-wider font-bold mb-1" data-id="element-348" data-mp="15m9m8ca2cfat">
                        Estudió en:
                      </p>
                      <p className="text-sm text-gray-600 font-medium" data-id="element-349" data-mp="vuc4emojup34">
                        {t.study}
                      </p>
                    </div>
                  </div>
                </motion.div>)}
            </div>

            {/* Visual indicators for mobile */}
            <div className="flex justify-center gap-2 mt-2 md:hidden" data-id="element-350" data-mp="350kgnvhwfy3p">
              {testimonialData.map((_, i) => <div key={i} className="w-1.5 h-1.5 rounded-full bg-gray-300" data-id="element-351" data-mp="39z5jm2ojcvvu" data-mpi={i}></div>)}
            </div>

            <p className="text-center text-sm text-gray-400 mt-4 flex items-center justify-center gap-2" data-id="element-352" data-mp="3exyvxrs36guf">
              <ChevronLeft className="w-4 h-4" data-id="element-353" data-mp="3jy4i4sfl7b54" /> Deslizá para ver más{' '}
              <ChevronRight className="w-4 h-4" data-id="element-354" data-mp="3nz6ft4tuvf4z" />
            </p>
          </motion.div>
        </div>
      </section>

      {/* PURCHASE SECTION - TWO CARDS */}
      <section id="compra" className="py-10 bg-white relative overflow-hidden" data-id="element-355" data-mp="3swott9mqckju">
        <div className="container mx-auto px-4 relative z-10" data-id="element-356" data-mp="1kn45fw9me27">
          <div className="max-w-4xl mx-auto" data-id="element-357" data-mp="6if6z4yg7kl6">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-8" data-id="element-358" data-mp="6i5enapxl1cb">
              <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-2 uppercase tracking-tight" data-id="element-359" data-mp="fh0ocws6otkd">
                ELEGÍ TU PACK Y EMPEZÁ HOY
              </h2>
              <p className="text-lg text-gray-600 font-medium" data-id="element-360" data-mp="1vy955zogtrfv">
                Acceso inmediato - Formato 100% digital en PDF - puede
                imprimirlo sin problema
              </p>
            </motion.div>

            {/* New Reference-style Offer Container */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.98
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            type: 'spring',
            stiffness: 100,
            damping: 20
          }} className={`max-w-md mx-auto rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.1)] border overflow-hidden mb-8 transition-colors duration-500 ${selectedOffer === 'solo' ? 'bg-[#EFF6FF] border-blue-200' : 'bg-white border-gray-100'}`} data-id="element-361" data-mp="2jod6g0e6lf3p">
              {/* Orange Header */}
              <div className="py-3 text-center bg-[#172554]" data-id="element-362" data-mp="vrdq8sn68e1b">
                <span className="text-white font-black text-lg uppercase tracking-wider" data-id="element-363" data-mp="zt7nss22l1x9">
                  DESCUENTO ESPECIAL
                </span>
              </div>

              <div className="p-4 md:p-6" data-id="element-364" data-mp="m1kdvpf7fuw0">
                {/* Offer Cards */}
                <div className="space-y-4 mb-8" data-id="element-365" data-mp="qzm896i2zgms">
                  {/* Solo Card */}
                  <motion.button whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} onClick={() => setSelectedOffer('solo')} className={`w-full relative flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-300 ${selectedOffer === 'solo' ? 'border-blue-500 bg-blue-50/30' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-id="element-366" data-mp="c3d8m3ua319u">
                    <div className="w-[35%] h-28 md:h-32 flex-shrink-0 bg-gray-50/50 rounded-xl overflow-hidden border border-gray-100 flex items-center justify-center p-1" data-id="element-367" data-mp="1xhit6b0z7d5o">
                      <img src="https://cdn.magicpatterns.com/uploads/jCvJXVKKtEEbjo3GpY2vii/ChatGPT_Image_6_feb_2026,_05_30_12_p.m..png" alt="Solo Maestro" className="w-full h-full object-contain drop-shadow-md" loading="lazy" decoding="async" width={140} height={112} data-id="element-368" data-mp="2cj7ubduwheey" />
                    </div>

                    <div className="flex-1 text-left min-w-0" data-id="element-369" data-mp="2glchbtqufcsm">
                      <h4 className="text-xs md:text-sm font-black text-gray-900 leading-tight uppercase break-words" data-id="element-370" data-mp="2vaig5glrr0zz">
                        SOLO MAESTRO ELÉCTRICO
                      </h4>
                      <p className="text-[9px] md:text-[10px] text-gray-500 font-bold uppercase mt-1" data-id="element-371" data-mp="2qdrzos61y3e1">
                        Sistema Avanzado + 6 Bonos
                      </p>
                    </div>

                    <div className="text-right flex flex-col items-end justify-center gap-2 flex-shrink-0" data-id="element-372" data-mp="19x5wvuv9gmew">
                      <span className="text-gray-900 font-black text-base md:text-lg" data-id="element-373" data-mp="15vu3rfd6hj1y">
                        $19.990
                      </span>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedOffer === 'solo' ? 'border-blue-500' : 'border-gray-300'}`} data-id="element-374" data-mp="17c7xccd1uy82">
                        {selectedOffer === 'solo' && <motion.div layoutId="offer-check" className="w-2.5 h-2.5 rounded-full bg-blue-500" data-id="element-375" data-mp="1gdxmfbjgll2o"></motion.div>}
                      </div>
                    </div>
                  </motion.button>

                  {/* V2 Card */}
                  <motion.button whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} onClick={() => setSelectedOffer('v2')} className={`w-full relative flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-300 ${selectedOffer === 'v2' ? 'border-[#FFD700] bg-[#FFFDE7]' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-id="element-376" data-mp="sntmpf9e65gy">
                    {/* Badge */}
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FFD700] text-black text-[10px] font-black px-3 py-1 rounded-full flex items-center gap-1 shadow-sm" data-id="element-377" data-mp="9w10q4sqpkiq">
                      <Star className="w-3 h-3 fill-black" data-id="element-378" data-mp="3tl5q7dwn1k0a" />
                      MÁS ELEGIDA
                    </div>

                    <div className="w-[35%] h-28 md:h-32 flex-shrink-0 bg-gray-50/50 rounded-xl overflow-hidden border border-gray-100 flex items-center justify-center p-1" data-id="element-379" data-mp="3ojgc1pe4cq7z">
                      <img src="https://cdn.magicpatterns.com/uploads/4Rvh5xfFPPUZZRESsYxpKM/e26b940a-400b-4c5b-97fa-ec344b9537f3.webp" alt="Maestro Eléctrico 2.0" className="w-full h-full object-contain drop-shadow-md" loading="lazy" decoding="async" width={140} height={112} data-id="element-380" data-mp="3fleexkxiahpz" />
                    </div>

                    <div className="flex-1 text-left min-w-0" data-id="element-381" data-mp="30wi1178rqahg">
                      <h4 className="text-xs md:text-sm font-black text-gray-900 leading-tight uppercase break-words" data-id="element-382" data-mp="2wtlnhuy354kd">
                        MAESTRO ELÉCTRICO 2.0
                      </h4>
                      <p className="text-[9px] md:text-[10px] text-gray-500 font-bold uppercase mt-1" data-id="element-383" data-mp="2i764mq2znm70">
                        Nueva Versión + 10 Bonos
                      </p>
                    </div>

                    <div className="text-right flex flex-col items-end justify-center gap-2 flex-shrink-0" data-id="element-384" data-mp="2898ge2l4ppee">
                      <span className="text-[#D4AF37] font-black text-base md:text-lg" data-id="element-385" data-mp="2d7jq8dbfbzwm">
                        $25.990
                      </span>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedOffer === 'v2' ? 'border-[#FFD700]' : 'border-gray-300'}`} data-id="element-386" data-mp="30yfq7xq1a1ey">
                        {selectedOffer === 'v2' && <motion.div layoutId="offer-check" className="w-2.5 h-2.5 rounded-full bg-[#FFD700]" data-id="element-387" data-mp="2r7krvuzijbd5"></motion.div>}
                      </div>
                    </div>
                  </motion.button>

                  {/* Combo Card */}
                  <motion.button whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} onClick={() => setSelectedOffer('combo')} className={`w-full relative flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-300 ${selectedOffer === 'combo' ? 'border-blue-500 bg-blue-50/30' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-id="element-388" data-mp="3omypxov0p5vf">
                    <div className="w-[35%] h-28 md:h-32 flex-shrink-0 bg-gray-50/50 rounded-xl overflow-hidden border border-gray-100 flex items-center justify-center p-1" data-id="element-389" data-mp="1i00zbucqlzm8">
                      <img src="https://cdn.magicpatterns.com/uploads/oczmUAW8GJp1by5ZQseLy4/56b749de-4a85-4345-a248-5c9dfa7d55de.webp" alt="Combo Industrial Completo" className="w-full h-full object-contain drop-shadow-md" loading="lazy" decoding="async" width={140} height={112} data-id="element-390" data-mp="pakebhrg5l01" />
                    </div>

                    <div className="flex-1 text-left min-w-0" data-id="element-391" data-mp="2eopdgflc12qd">
                      <h4 className="text-xs md:text-sm font-black text-gray-900 leading-tight uppercase break-words" data-id="element-392" data-mp="2er1vesr3bd3f">
                        COMBO INDUSTRIAL COMPLETO
                      </h4>
                      <p className="text-[9px] md:text-[10px] text-gray-500 font-bold uppercase mt-1" data-id="element-393" data-mp="2tqfgsj2n4lfd">
                        Sistema Completo + 15 Bonos
                      </p>
                    </div>

                    <div className="text-right flex flex-col items-end justify-center gap-2 flex-shrink-0" data-id="element-394" data-mp="32r3ypov5n5xj">
                      <span className="text-gray-900 font-black text-base md:text-lg" data-id="element-395" data-mp="3ciqc9ruyth3g">
                        $29.990
                      </span>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedOffer === 'combo' ? 'border-blue-500' : 'border-gray-300'}`} data-id="element-396" data-mp="3hgji2jscw937">
                        {selectedOffer === 'combo' && <motion.div layoutId="offer-check" className="w-2.5 h-2.5 rounded-full bg-blue-500" data-id="element-397" data-mp="3qhzji21710va"></motion.div>}
                      </div>
                    </div>
                  </motion.button>
                </div>

                {/* Social Proof Image */}
                <div className="mb-6 rounded-2xl overflow-hidden shadow-lg border border-gray-200" data-id="element-398" data-mp="1ol8sf2eyqzj4">
                  
                </div>

                {selectedOffer === 'combo' || selectedOffer === 'v2' ? <div className="rounded-3xl overflow-hidden border border-gray-200 shadow-sm mb-4" data-id="element-399" data-mp="1eue9a6x0kg1w">
                    <img src={selectedOffer === 'combo' ? 'https://cdn.magicpatterns.com/uploads/uVozf6vqbAq1WkJwQih8vs/f5dd2e07-fd54-4ead-8fc3-1ac947a90263.webp' : 'https://cdn.magicpatterns.com/uploads/1kPHPyU8ZfyrYYAHpB6LBD/5bd0dcee-5522-43a7-998b-29f994ff7e4f.webp'} alt="¿Qué estás llevando?" className="w-full h-auto object-contain" loading="lazy" decoding="async" width={400} height={300} data-id="element-400" data-mp="1audl3uk44qhn" />
                  </div> : <div className="bg-[#F8F9FA] rounded-3xl p-6 md:p-8 border border-gray-200 shadow-sm pt-[10px] pb-[10px] space-y-[7px]" data-id="element-401" data-mp="1tj0n246zszr0">
                    <h5 className="font-black text-[11px] uppercase tracking-[0.25em] text-center text-gray-400 mb-[20px]" data-id="element-402" data-mp="n1x0pdcwkmf6">
                      ¿QUÉ ESTÁS LLEVANDO?
                    </h5>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-[15px]" data-id="element-403" data-mp="3fx6zhrjtv198">
                      {/* GROUP A: CORE */}
                      <div data-id="element-404" data-mp="3ow0w3hx5rrms">
                        <div className="space-y-[7px]" data-id="element-405" data-mp="3ovsfbexx026b">
                          {['Manual Maestro Eléctrico', 'Manual PLC Master (Industrial)'].filter(item => selectedOffer === 'combo' || item !== 'Manual PLC Master (Industrial)').map((item, i) => <div key={i} className="flex items-start gap-2" data-id="element-406" data-mp="2fxzhp2yvrbw" data-mpi={i}>
                                <Check className="w-3.5 h-3.5 text-[#00A859] stroke-[4px] mt-0.5 flex-shrink-0" data-id="element-407" data-mp="7eho5x8y3h7y" />
                                <span className="font-bold text-[12px] text-gray-700 leading-tight" data-id="element-408" data-mp="bifkk06vydxb">
                                  {item}
                                </span>
                              </div>)}
                        </div>
                      </div>

                      {/* GROUP B: BONOS */}
                      <div data-id="element-409" data-mp="2i7u8592daj4b">
                        <div className="space-y-[7px]" data-id="element-410" data-mp="2ao2vhz8s75xu">
                          {['Bono 1: Eficiencia en Motores', 'Bono 2: Plantillas Presupuesto', 'Bono 3: Protocolo de Energia', 'Bono 4: Curso Paneles Solares', 'Bono 5: Reglamentaciones AEA'].map((item, i) => <div key={i} className="flex items-start gap-2" data-id="element-411" data-mp="20zb6nzz0p1r5" data-mpi={i}>
                              <Check className="w-3.5 h-3.5 text-yellow-500 stroke-[4px] mt-0.5 flex-shrink-0" data-id="element-412" data-mp="1vzot1pfa1jht" />
                              <span className="font-bold text-[12px] text-gray-700 leading-tight" data-id="element-413" data-mp="2tn6yordn65hl">
                                {item}
                              </span>
                            </div>)}
                        </div>
                      </div>

                      {/* GROUP C: AVANZADO */}
                      <div className="md:col-span-2 pt-[0px]" data-id="element-414" data-mp="2fme303blt4cw">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-[7px]" data-id="element-415" data-mp="189ca7jihu2cg">
                          <div className="flex items-center gap-2 bg-blue-50 p-2 rounded-lg border border-blue-100" data-id="element-416" data-mp="1480lrhbcvn5u">
                            <div className="bg-blue-600 rounded-full p-1" data-id="element-417" data-mp="15oms867co7n5">
                              <Award className="w-3 h-3 text-white" data-id="element-418" data-mp="1ao94acbfzhud" />
                            </div>
                            <span className="font-black text-[12px] text-blue-800 leading-tight" data-id="element-419" data-mp="vr07v47a7nvs">
                              EXTRA: Certificado Profesional
                            </span>
                          </div>

                          {selectedOffer === 'combo' && ['Bono 7: Curso completo de SCADA', 'Bono 8: Módulo extra de PLC', 'Bono 9: Control de procesos', 'Bono 10: Variadores y Sensores'].map((item, i) => <div key={i} className="flex items-start gap-2" data-id="element-420" data-mp="d0qx35xfinsh" data-mpi={i}>
                                <Check className="w-3.5 h-3.5 text-[#00A859] stroke-[4px] mt-0.5 flex-shrink-0" data-id="element-421" data-mp="cynwjrynhc9i" />
                                <span className="font-bold text-[12px] text-gray-700 leading-tight" data-id="element-422" data-mp="3zsoeac1ps3k">
                                  {item}
                                </span>
                              </div>)}
                        </div>
                      </div>
                    </div>
                  </div>}

                {/* Price Summary */}
                <div className="text-center mt-8 mb-6" data-id="element-423" data-mp="3gkzi5nlnbmj7">
                  <p className="text-sm text-[#FF5C5C] font-bold mb-1" data-id="element-424" data-mp="3ujpf938gtm7k">
                    Antes{' '}
                    <span className="line-through" data-id="element-425" data-mp="3pjl8w619u7v2">
                      {selectedOffer === 'solo' ? '$39.980' : selectedOffer === 'v2' ? '$49.980' : '$59.980'}{' '}
                      ARS
                    </span>{' '}
                    <span className="bg-[#FF5C5C] text-white text-[10px] px-2 py-0.5 rounded-full ml-1 animate-pulse" data-id="element-426" data-mp="3c3h1q4imby3">
                      50% OFF SOLO POR HOY
                    </span>
                  </p>
                  <div className="flex flex-col items-center justify-center" data-id="element-427" data-mp="d37xku9ua4v1">
                    <div className="flex items-baseline justify-center gap-2" data-id="element-428" data-mp="r3iotyf29lgq">
                      <motion.span key={selectedOffer} initial={{
                      scale: 0.9,
                      opacity: 0
                    }} animate={{
                      scale: 1,
                      opacity: 1
                    }} className="text-7xl md:text-8xl font-[1000] text-[#00A859] tracking-tighter leading-none drop-shadow-sm" data-id="element-429" data-mp="m3w9njfo9v5a">
                        {selectedOffer === 'solo' ? '$19.990' : selectedOffer === 'v2' ? '$24.990' : '$29.990'}
                      </motion.span>
                      <span className="font-black text-xl text-gray-400" data-id="element-430" data-mp="n7el3tesx2pz">
                        ARS
                      </span>
                    </div>
                  </div>
                </div>

                {/* Installments Badge */}
                <div className="flex justify-center mb-8" data-id="element-431" data-mp="w600xgsw23n2">
                  <div className="bg-[#E8F5E9] text-[#2E7D32] border border-[#A5D6A7] px-5 py-2.5 rounded-2xl flex items-center gap-2 text-sm font-black animate-badge-reflection shadow-sm" data-id="element-432" data-mp="117fk13bia02t">
                    <CreditCard className="w-4 h-4" data-id="element-433" data-mp="2ckrsxsm4h8vu" />
                    HASTA 3 CUOTAS SIN INTERÉS
                  </div>
                </div>

                {/* CTA Button */}
                <div className="bg-[#E8F5E9] p-1.5 rounded-[22px] shadow-sm" data-id="element-434" data-mp="3s4sg58e2juav">
                  <motion.button whileHover={{
                  scale: 1.01
                }} whileTap={{
                  scale: 0.98
                }} onClick={selectedOffer === 'combo' ? handleComboPurchaseClick : selectedOffer === 'v2' ? handleV2PurchaseClick : handlePurchaseClick} className="w-full bg-gradient-to-b from-[#22C55E] to-[#16A34A] hover:from-[#16A34A] hover:to-[#15803d] text-white rounded-[18px] py-6 px-4 shadow-[0_6px_0_#14532d,0_15px_20px_rgba(0,0,0,0.3)] active:shadow-[0_0px_0_#14532d,0_0px_0_rgba(0,0,0,0.2)] active:translate-y-[6px] transition-all flex flex-col items-center justify-center gap-1 relative overflow-hidden animate-cta-pulse btn-shine border border-[#4ade80]" data-id="element-435" data-mp="3s6v3tlrr1bcf">
                    <span className="text-2xl md:text-3xl font-black uppercase tracking-tight relative z-10" data-id="element-436" data-mp="xogqrfkia86c">
                      ¡SÍ! QUIERO MEJORAR MI CARRERA
                    </span>
                  </motion.button>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 mt-4 mb-2" data-id="element-437" data-mp="2wvx94w4u9ofx">
                    <div className="flex items-center gap-1.5 bg-white/60 px-3 py-1 rounded-full border border-green-200/50" data-id="element-438" data-mp="2i7k99o2mrlg5">
                      <div className="bg-green-100 p-1 rounded-full" data-id="element-439" data-mp="2n5vibwl3prxb">
                        <Zap className="w-3.5 h-3.5 text-green-600" data-id="element-440" data-mp="3aw7yfuqi2uyz" />
                      </div>
                      <span className="text-xs font-bold text-gray-700 uppercase tracking-wide" data-id="element-441" data-mp="3ft84w6r9hzqm">Acceso Inmediato</span>
                    </div>
                    <div className="hidden sm:block w-1 h-1 rounded-full bg-green-300" data-id="element-442" data-mp="31wkv33e7nf3a"></div>
                    <div className="flex items-center gap-1.5 bg-white/60 px-3 py-1 rounded-full border border-green-200/50" data-id="element-443" data-mp="35y6gjd4t91p3">
                      <div className="bg-green-100 p-1 rounded-full" data-id="element-444" data-mp="1ugy90iyvb5gp">
                        <Shield className="w-3.5 h-3.5 text-green-600" data-id="element-445" data-mp="1uety3h2jx66x" />
                      </div>
                      <span className="text-xs font-bold text-gray-700 uppercase tracking-wide" data-id="element-446" data-mp="1mziakh80cd5g">Garantía de 30 Días</span>
                    </div>
                  </div>
                </div>

                {/* Social Proof Image */}
                <div className="mt-6 rounded-2xl overflow-hidden shadow-lg border border-gray-200" data-id="element-447" data-mp="1rwryfsfr0gr6">
                  <img src="https://cdn.magicpatterns.com/uploads/bMEmGhKJnbWQozQy9C37z7/ChatGPT_Image_8_ago_2026,_23_17_17.png" alt="Más de 3000 alumnos con el curso completo" className="w-full h-auto object-cover" loading="lazy" data-id="element-448" data-mp="146o308cprnb9" />
                </div>

                {/* Social Proof Widget */}
                <div className="mt-6 rounded-2xl overflow-hidden border border-gray-200 shadow-lg bg-gray-50 relative" data-id="element-449" data-mp="196afgfkpwzj0">
                  {/* Date Banner */}
                  <div className="px-4 py-3 text-center border-b border-yellow-500/20 relative z-10" data-id="element-450" data-mp="leli2kt1hkv7">
                    <p className="text-gray-900 text-xs md:text-sm font-black tracking-wide uppercase" data-id="element-451" data-mp="gf90aeh0z3zh">
                      OFERTA VÁLIDA SOLO POR HOY:{' '}
                      <span className="text-yellow-600" data-id="element-452" data-mp="3w3k9g2yffgxv">
                        {weekdayDate.toUpperCase()}
                      </span>
                    </p>
                  </div>

                  {/* Viewers & Cupos Bar */}
                  <div className="grid grid-cols-2 divide-x divide-gray-200 relative z-10" data-id="element-453" data-mp="8z4h3wdofvuc">
                    {/* Viewers */}
                    <div className="flex items-center gap-3 px-4 py-4 justify-center" data-id="element-454" data-mp="ivzcw7ikfi4x">
                      <div className="flex items-center gap-1.5" data-id="element-455" data-mp="itmnxaee47id">
                        <span className="relative flex h-3 w-3" data-id="element-456" data-mp="nn7ona7dbfl9">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" data-id="element-457" data-mp="sm23y264mm0m"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" data-id="element-458" data-mp="woyt6bq69gdj"></span>
                        </span>
                        <span className="text-green-600 text-2xl md:text-3xl font-black" data-id="element-459" data-mp="2m4hw29cgriil">
                          {viewers}
                        </span>
                      </div>
                      <div className="text-left" data-id="element-460" data-mp="2h7iz7j1f0xgp">
                        <p className="text-gray-600 text-[10px] md:text-xs font-bold leading-tight uppercase" data-id="element-461" data-mp="2d4ur5dkizuks">
                          PERSONAS
                          <br data-id="element-462" data-mp="286t6io7gbn77" />
                          MIRANDO
                          <br data-id="element-463" data-mp="34tpkv83ribpe" />
                          EN ESTE MOMENTO
                        </p>
                      </div>
                    </div>

                    {/* Cupos */}
                    <div className="flex items-center gap-3 px-4 py-4 justify-center" data-id="element-464" data-mp="2w1nlze9cd3h8">
                      <div className="flex items-center gap-1.5" data-id="element-465" data-mp="2r4dxq1xouxuz">
                        <span className="text-red-600 text-2xl md:text-3xl font-black" data-id="element-466" data-mp="3oizx2c4aueir">
                          {selectedOffer === 'combo' ? comboSpots : selectedOffer === 'v2' ? 5 : urgencySpots}
                        </span>
                      </div>
                      <div className="text-left" data-id="element-467" data-mp="3bmk64w9gtwal">
                        <p className="text-gray-600 text-[10px] md:text-xs font-bold leading-tight uppercase" data-id="element-468" data-mp="37kq70sex50cj">
                          CUPOS
                          <br data-id="element-469" data-mp="3ll0zfzvvmmzt" />
                          PROMOCIONALES
                          <br data-id="element-470" data-mp="3gkvfz6zrg6st" />
                          DISPONIBLES
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Buyer Counter */}
                  <div className="px-4 py-4 text-center border-t border-gray-200 relative z-10" data-id="element-471" data-mp="3qi9libk5bb6c">
                    <motion.p key={selectedOffer === 'combo' ? comboBuyers : selectedOffer === 'v2' ? comboBuyers - 1000 : mainBuyers} initial={{
                    scale: 1.1,
                    opacity: 0.7
                  }} animate={{
                    scale: 1,
                    opacity: 1
                  }} transition={{
                    duration: 0.3
                  }} className="text-green-600 text-4xl md:text-5xl font-black tracking-tight" data-id="element-472" data-mp="84a159kg6jhs">
                      {(selectedOffer === 'combo' ? comboBuyers : selectedOffer === 'v2' ? comboBuyers - 1000 : mainBuyers).toLocaleString('es-AR')}
                    </motion.p>
                    <p className="text-gray-600 text-xs md:text-sm font-bold uppercase tracking-wider mt-1" data-id="element-473" data-mp="mueg4fcac7q4">
                      PROFESIONALES YA ACCEDIERON
                    </p>
                  </div>
                </div>

                {/* Trust Badges Section */}
                <div className="mt-8 flex flex-col items-center gap-6 animate-fade-in-up-soft" data-id="element-474" data-mp="huibcdvrkc9i">
                  {/* Placeholder for the new images with adjusted heights */}
                  <img src="https://cdn.magicpatterns.com/uploads/45AKbXKjJnZm5Z1fHy751C/capture_260410_163646.png" alt="Pago Seguro Garantizado" className="w-full max-w-[320px] h-auto object-contain brightness-105" loading="lazy" decoding="async" width={320} height={80} data-id="element-475" data-mp="91osy7l0swrf" />
                  <div className="flex items-center justify-center md:gap-10 gap-[10px]" data-id="element-476" data-mp="3c2yu6auwgejv">
                    <img src="https://cdn.magicpatterns.com/uploads/6sMJ49nAT1B4ubV5kxhyZE/png-clipart-safe-purchase-payment-security-shop-shopping-online-store-e-commerce-sign-green-texta.png" alt="Compra Segura" className="h-20 md:h-24 w-auto object-contain" loading="lazy" decoding="async" width={100} height={80} data-id="element-477" data-mp="37cs4s8skcq0k" />
                    <img src="https://cdn.magicpatterns.com/uploads/xuS8xz2qt356pzvFYDWisp/Selo_de_Garantia_de_7_Dias_PNG_Transparente_Sem_Fundo.png" alt="Garantía de 7 Días" className="h-32 md:h-40 w-auto object-contain" loading="lazy" decoding="async" width={120} height={128} data-id="element-478" data-mp="3v2cpox8a1xch" />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* MercadoLíder Platinum Trust Seal */}
            <div className="flex justify-center" data-id="element-479" data-mp="29pjbhrmbmco0">
              <img src="https://cdn.magicpatterns.com/uploads/wihdMwuqBpQKvri8XHgUZZ/Gemini_Generated_Image_jbp7w2jbp7w2jbp7.png" alt="MercadoLíder Platinum - 8413 ventas" className="w-full max-w-2xl object-contain" loading="lazy" decoding="async" width={672} height={100} data-id="element-480" data-mp="24pn91wt9utak" />
            </div>

            {/* Lifetime Access Bonus Section */}
            <div className="mt-12 mb-8 flex justify-center px-4" data-id="element-481" data-mp="u50lewd7ies3">
              <div className="w-full max-w-2xl bg-white rounded-[12px] border border-gray-200 shadow-2xl overflow-hidden relative group" data-id="element-482" data-mp="yw8yc2dqwia0">
                {/* Subtle Glow Effect */}
                <div className="absolute inset-0 bg-gray-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700" data-id="element-483" data-mp="17x716hcg6jr0"></div>

                <div className="p-8 md:p-12 flex flex-col items-center text-center relative z-10 pl-[15px] pr-[15px]" data-id="element-484" data-mp="1cw0d45c362p2">
                  {/* Authority Seal */}
                  <div className="relative mb-8" data-id="element-485" data-mp="3enmzftl199f7">
                    <div className="absolute inset-0 bg-yellow-500/10 rounded-full blur-3xl animate-pulse" data-id="element-486" data-mp="3eoydqnc3tzkk"></div>
                    <div className="relative w-40 h-40 md:w-48 md:h-48 rounded-full shadow-[0_0_50px_rgba(234,179,8,0.2)] border-4 border-yellow-500/10 overflow-hidden" data-id="element-487" data-mp="3jn9m0tmr420o">
                      <img src="https://cdn.magicpatterns.com/uploads/oLERSYzpMkuFusC7XS9pGH/Gemini_Generated_Image_ajg2w8ajg2w8ajg2.png" alt="Sello de Acceso Vitalicio" className="w-full h-full object-cover" loading="lazy" decoding="async" width={192} height={192} data-id="element-488" data-mp="1905wc74v1utg" />
                    </div>
                  </div>

                  {/* Text Hierarchy */}
                  <span className="text-green-600 text-[10px] md:text-xs font-black tracking-[0.2em] uppercase mb-3" data-id="element-489" data-mp="v5lngiui2npm">
                    BONO EXTRA EXCLUSIVO
                  </span>

                  <h3 className="text-gray-900 text-3xl md:text-4xl font-bold mb-4 tracking-tight" data-id="element-490" data-mp="142e4fd47u8p9">
                    Acceso Vitalicio
                  </h3>

                  <p className="text-gray-600 text-sm md:text-base leading-relaxed max-w-lg mb-8" data-id="element-491" data-mp="bixe515wsc85">
                    Olvidate de las mensualidades. Con un solo pago tenés acceso
                    de por vida a todo el material y a las futuras
                    actualizaciones sin pagar un centavo más.
                  </p>

                  {/* Status Component */}
                  <div className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-green-50 border border-green-200 shadow-sm" data-id="element-492" data-mp="3jm6okuy7ll5g">
                    <Check className="w-4 h-4 text-green-700 stroke-[3px]" data-id="element-493" data-mp="3ol9ipkcni6yn" />
                    <span className="text-green-700 text-xs md:text-sm font-black uppercase tracking-widest" data-id="element-494" data-mp="g5zloqnmbrxq">
                      INCLUIDO GRATIS
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT PREVIEW */}
      <section className="py-8 px-4 bg-white" data-id="element-495" data-mp="b40grcc8cmyo">
        <div className="container mx-auto max-w-6xl" data-id="element-496" data-mp="2qx99t1t7i6hg">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-497" data-mp="2qvodb6nc0dgy">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-498" data-mp="12f6c8fvmzg2h">
              Así es el material por dentro
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto uppercase font-bold" data-id="element-499" data-mp="smsbnqgaofhw">
              CONSIGUE MÁS DE{' '}
              <span className="text-blue-600" data-id="element-500" data-mp="nnxrol6lckw7">300 PÁGINAS A TODO COLOR</span>{' '}
              CON ILUSTRACIONES REALISTAS
            </p>
          </motion.div>

          <div className="space-y-16" data-id="element-501" data-mp="en9chn5goehq">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} className="flex justify-center" data-id="element-502" data-mp="em7roqyn8dmt">
              <div className="w-full max-w-2xl bg-gray-900 rounded-xl overflow-hidden shadow-2xl" data-id="element-503" data-mp="242gzek1dhijr">
                <LazyVideo src="https://cdn.magicpatterns.com/uploads/o7CL5oytsWzC7MznGGznLq/0427 (1).mp4" className="w-full" data-id="element-504" data-mp="1z34a7sn13ze0" />
              </div>
            </motion.div>
            <ImageCarousel images={contentImages} data-id="element-505" data-mp="26lathg61dzo7" />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS - WhatsApp style, right before FAQ */}
      <section className="py-8 px-4 bg-gradient-to-b from-gray-100 to-gray-50" data-id="element-506" data-mp="2ge9igz5g3s8c">
        <div className="container mx-auto max-w-2xl" data-id="element-507" data-mp="2lcb0rhv1rnir">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-8" data-id="element-508" data-mp="2le4chbkioxxm">
            <div className="flex items-center justify-center gap-1 mb-2" data-id="element-509" data-mp="1xmqh0wr2km8f">
              {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-4 h-4 fill-yellow-400 text-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.6)]" data-id="element-510" data-mp="22la5b3t4ya3y" />)}
              <span className="text-sm font-bold ml-2 tracking-wide text-[#0A0090]" data-id="element-511" data-mp="390khw6hbfn3k">
                4.9/5 (3,251+ Alumnos)
              </span>
            </div>
            <h2 className="text-lg font-bold text-gray-800 uppercase" data-id="element-512" data-mp="2zd4pfb57idy">
              MÁS DE 6.250 ELECTRICISTAS SATISFECHOS
            </h2>
            <p className="text-xs text-gray-500 mt-1" data-id="element-513" data-mp="3u6plvuc0zvtv">
              4,9/5 — 487 reseñas verificadas
            </p>
          </motion.div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="space-y-4 mb-8" data-id="element-514" data-mp="cr9gft65j70n">
            {allChatReviews.slice(0, visibleTestimonials).map((t, i) => <motion.div key={i} variants={itemVariants} className="flex items-start gap-3" data-id="element-515" data-mp="hojdnuzs8mz1" data-mpi={i}>
                <div className="w-9 h-9 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0" data-id="element-516" data-mp="vo0spina0lv7">
                  {t.name.charAt(0)}
                </div>
                <div className="flex-grow" data-id="element-517" data-mp="2xf5kqft4fog2">
                  <div className="flex items-center gap-2 mb-1" data-id="element-518" data-mp="2xg6xcopscizw">
                    <span className="font-bold text-sm text-gray-800" data-id="element-519" data-mp="2sguai3ywzbxb">
                      {t.name}
                    </span>
                    {t.verified && <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium" data-id="element-520" data-mp="17ys31zxt2l58">
                        ✓ Verificado
                      </span>}
                  </div>
                  <div className="chat-bubble" data-id="element-521" data-mp="y870k1f0gwh1">
                    <p className="text-sm text-gray-800 leading-relaxed" data-id="element-522" data-mp="1371otivqzd8j">
                      {t.msg}
                    </p>
                    <p className="text-[10px] text-gray-400 text-right mt-1" data-id="element-523" data-mp="p6zdygluxk2s">
                      {t.time}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </motion.div>

          {visibleTestimonials < allChatReviews.length && <div className="text-center" data-id="element-524" data-mp="k7w66oehszr4">
              <button onClick={showMoreTestimonials} className="btn-shine bg-gray-100 border border-gray-200 text-gray-600 hover:bg-gray-200 font-medium py-2.5 px-6 rounded-lg transition-colors text-sm" data-id="element-525" data-mp="k8xsxq4vfaoo">
                Cargar 5 comentarios más
              </button>
            </div>}

          <ReviewForm onSubmitReview={handleSubmitReview} data-id="element-526" data-mp="3i5bs1anvsji0" />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-8 px-4 bg-white" data-id="element-527" data-mp="3mz6bqe3hkyh3">
        <div className="container mx-auto max-w-3xl" data-id="element-528" data-mp="396gm7zor1bf8">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-529" data-mp="3981h5qez6wdm">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-530" data-mp="5nncjxz3d6qc">
              Resolvé acá tus dudas
            </h2>
            <p className="text-lg text-gray-600" data-id="element-531" data-mp="3rx7u0wt38tri">
              Hacé clic para ver la respuesta que necesitás
            </p>
          </motion.div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="space-y-3" data-id="element-532" data-mp="ok7wen0x2iw">
            <motion.div variants={itemVariants} data-id="element-533" data-mp="2lgvzr6tdzw17">
              <FAQItem question="¿Qué tipo de contenido incluye CONTROL ELÉCTRICO?" answer="Incluye un manual completo de control eléctrico, con explicación clara de la lógica de los circuitos, diagramas reales y ejemplos aplicados al trabajo profesional." data-id="element-534" data-mp="2lejd4gcooxht" />
            </motion.div>
            <motion.div variants={itemVariants} data-id="element-535" data-mp="2mx02ke0e300k">
              <FAQItem question="¿Este material es solo para electricistas con experiencia?" answer="Está pensado tanto para electricistas con experiencia como para técnicos que quieren entender de verdad el control eléctrico y dejar de trabajar por repetición o memoria." data-id="element-536" data-mp="2hy6pgmp0lb0x" />
            </motion.div>
            <motion.div variants={itemVariants} data-id="element-537" data-mp="23zgrl4udf7bi">
              <FAQItem question="¿Voy a aprender a interpretar diagramas eléctricos?" answer="Sí. El foco principal es ayudarte a interpretar y entender diagramas de mando y control eléctrico, no solo copiarlos." data-id="element-538" data-mp="2408d1mu97oo2" />
            </motion.div>
            <motion.div variants={itemVariants} data-id="element-539" data-mp="16jt2rl5zz1mh">
              <FAQItem question="¿Incluye ejemplos prácticos reales?" answer="Sí. El contenido está orientado a situaciones reales de tableros, motores y circuitos usados en el trabajo diario." data-id="element-540" data-mp="16j04z9p0nmgh" />
            </motion.div>
            <motion.div variants={itemVariants} data-id="element-541" data-mp="3bist7x8ng1rd">
              <FAQItem question="¿En qué formato recibo el material?" answer="Todo el contenido se entrega en formato digital (PDF) con acceso inmediato." data-id="element-542" data-mp="3gbc7kwf5v0x8" />
            </motion.div>
            <motion.div variants={itemVariants} data-id="element-543" data-mp="2nn6oee3r9jzi">
              <FAQItem question="¿Puedo imprimir el material?" answer="Sí. Podés imprimirlo para uso personal." data-id="element-544" data-mp="2norglx2lkqu7" />
            </motion.div>
            <motion.div variants={itemVariants} data-id="element-545" data-mp="2xl4c90hpderl">
              <FAQItem question="¿Cuánto tarda en llegar el material después de la compra?" answer="El acceso es inmediato una vez acreditado el pago." data-id="element-546" data-mp="2xjjdszs9pbog" />
            </motion.div>
            <motion.div variants={itemVariants} data-id="element-547" data-mp="cvnbgiouz4bo">
              <FAQItem question="¿Necesito conocimientos avanzados de electrónica o automatización?" answer="No. El material explica los conceptos de forma clara y progresiva, enfocándose en criterio y lógica." data-id="element-548" data-mp="cx83o1npab6d" />
            </motion.div>
            <motion.div variants={itemVariants} data-id="element-549" data-mp="1xced6hcyoda0">
              <FAQItem question="¿El contenido me sirve para trabajar con más seguridad?" answer="Sí. El objetivo es que entiendas qué está pasando en el circuito para trabajar con más seguridad y confianza." data-id="element-550" data-mp="1xdpmewp6ed8i" />
            </motion.div>
            <motion.div variants={itemVariants} data-id="element-551" data-mp="1inry8gr8xnjb">
              <FAQItem question="¿Tengo acceso para siempre al material?" answer="Sí. El acceso es permanente." data-id="element-552" data-mp="1ipl43tu1c3q9" />
            </motion.div>
            <motion.div variants={itemVariants} data-id="element-553" data-mp="19nbwbbq5iogk">
              <FAQItem question="¿Es un pago único?" answer="Sí. Pagás una sola vez y accedés a todo el contenido y bonos incluidos." data-id="element-554" data-mp="19o4u3n74u3mk" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 px-4 bg-white border-t border-gray-100" data-id="element-555" data-mp="33z0edmroj756">
        <div className="container mx-auto max-w-2xl text-center" data-id="element-556" data-mp="2z2t4xk02rdih">
          <p className="text-[#FF0000] font-black text-sm md:text-base mb-6 uppercase tracking-wider" data-id="element-557" data-mp="1vr49r7a112ca">
            APROVECHA AHORA: NO ENCONTRARÁS ESTE PRECIO MÁS ADELANTE.
          </p>
          <div className="bg-[#E8F5E9] p-1.5 rounded-[22px] shadow-sm" data-id="element-558" data-mp="1mr7doklb5177">
            <motion.button whileHover={{
            scale: 1.01
          }} whileTap={{
            scale: 0.98
          }} onClick={() => {
            document.getElementById('compra')?.scrollIntoView({
              behavior: 'smooth'
            });
          }} className="w-full bg-gradient-to-b from-[#22C55E] to-[#16A34A] hover:from-[#16A34A] hover:to-[#15803d] text-white rounded-[18px] py-6 px-4 shadow-[0_6px_0_#14532d,0_15px_20px_rgba(0,0,0,0.3)] active:shadow-[0_0px_0_#14532d,0_0px_0_rgba(0,0,0,0.2)] active:translate-y-[6px] transition-all flex flex-col items-center justify-center gap-1 relative overflow-hidden animate-cta-pulse btn-shine border border-[#4ade80]" data-id="element-559" data-mp="1mt0jxys0djeo">
              <span className="text-2xl md:text-3xl font-black uppercase tracking-tight relative z-10" data-id="element-560" data-mp="z369fnv5w6xl">
                ¡SÍ! QUIERO MEJORAR MI CARRERA
              </span>
            </motion.button>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 mt-4 mb-2" data-id="element-561" data-mp="aibe9m5p5jus">
              <div className="flex items-center gap-1.5 bg-white/60 px-3 py-1 rounded-full border border-green-200/50" data-id="element-562" data-mp="pb4u58ovs1zz">
                <div className="bg-green-100 p-1 rounded-full" data-id="element-563" data-mp="ydw8bg955bvb">
                  <Zap className="w-3.5 h-3.5 text-green-600" data-id="element-564" data-mp="yc2yxt758djm" />
                </div>
                <span className="text-xs font-bold text-gray-700 uppercase tracking-wide" data-id="element-565" data-mp="tejiqnqvxqp1">Acceso Inmediato</span>
              </div>
              <div className="hidden sm:block w-1 h-1 rounded-full bg-green-300" data-id="element-566" data-mp="134w2idqvlfu4"></div>
              <div className="flex items-center gap-1.5 bg-white/60 px-3 py-1 rounded-full border border-green-200/50" data-id="element-567" data-mp="1378iild2pg4j">
                <div className="bg-green-100 p-1 rounded-full" data-id="element-568" data-mp="1d3busk1n2v57">
                  <Shield className="w-3.5 h-3.5 text-green-600" data-id="element-569" data-mp="1d4wne44e840f" />
                </div>
                <span className="text-xs font-bold text-gray-700 uppercase tracking-wide" data-id="element-570" data-mp="1k7tixe5t0t7b">Garantía de 30 Días</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gray-900 text-white py-12 px-4 mb-24 md:mb-32" data-id="element-571" data-mp="1u2d38ozcq8qd">
        <div className="container mx-auto max-w-6xl flex flex-col items-center" data-id="element-572" data-mp="1ywpnh18njo36">
          <img src="https://cdn.magicpatterns.com/uploads/3vpvfNg4VHgy4RAHHhGhrN/Diseno_sin_titulo.webp" alt="Maestro Eléctrico Logo" className="w-24 h-24 md:w-32 md:h-32 object-contain mb-6 opacity-90" loading="lazy" data-id="element-573" data-mp="22vq4qfbheio2" />
          <p className="text-center text-sm" data-id="element-574" data-mp="2cvgxocke8pm1">
            © 2025 Maestro Eléctrico. Todos los derechos reservados.
          </p>
          <p className="text-center text-sm mt-2" data-id="element-575" data-mp="2hro5yc09id75">
            Diagramas y circuitos de mando para profesionales.
          </p>
          <p className="text-center text-sm mt-4 text-gray-400" data-id="element-576" data-mp="nfhjgh1x49zj">
            Soporte: soportecodexlabs@gmail.com
          </p>
        </div>
      </footer>
    </div>;
}